// Die Spielszene: Feld, Schiff, Effekte, Mitspieler.
//
// Zwei Betriebsarten, **eine** Simulation:
//
// * **Solo** — die Szene ist ihre eigene Autoritaet: sie bucht Schaden, laesst
//   Brocken zerspringen und schreibt die Beute in den Spielstand.
// * **Online** — die Szene zeichnet dieselben Strahlen, bucht aber **nichts**.
//   Schaden, Zerstoerung und Beute kommen als Nachrichten vom Server. Der
//   Unterschied ist genau eine Abfrage (`store.authoritative`), und das ist
//   Absicht: zwei Simulationen waeren zwei Wahrheiten.
//
// **Zeichnen mutiert keinen Spielzustand** (Gate FG-17). Alle `sync…`-Methoden
// lesen ausschliesslich; was sich veraendert, veraendert sich in `step`.

import Phaser from 'phaser';

import { bus } from '../core/bus';
import { distStr, fmt } from '../core/format';
import { TAU, clamp, hexToInt, mulHex } from '../core/math';
import { chance } from '../core/rng';
import {
  anomalyKind, balancing, cacheById, enemy, fleetOrder, gearById, planetById, planets, probeLog,
  resource, resources, sideQuest,
  worldLimit, zoneAtDist, zonesFor,
} from '../data/balancing';
import { sfx, unlockAudio } from '../audio/synth';
import { AnomalyField } from '../sim/anomalies';
import { applyBlast, type BlastSink } from '../sim/blast';
import { nearestFront, type BearingHit } from '../sim/bearing';
import { compassMark, type CompassMark } from '../sim/compass';
import { pickGuidedTarget } from '../sim/guided';
import type { Rock } from '../sim/field';
import { Ship, type ShipInput } from '../sim/ship';
import {
  bearingRadius, courseReason, steerTo, warpProgress, type Course, type Steering,
} from '../sim/navigation';
import {
  blockedCacheAt, cacheAt, cacheHint, cacheSignal, findCaches, openCache,
  type CacheFind, type CacheSignal,
} from '../sim/caches';
import { findGear, gearAt, hookProgress } from '../sim/gear';
// Ausdruecklich benannt: `Cache` heisst im DOM etwas ganz anderes, und ohne
// diesen Import zieht TypeScript still den falschen Typ.
import type { Cache } from '../data/types';
import {
  advanceSide, sideQuestLabel, stepPoint, type Point,
} from '../sim/sidequests';
import { World } from '../sim/world';
import { HostileField, enemyDamage, type Hostile } from '../sim/hostiles';
import { ProbeField, type Probe } from '../sim/probes';
import { boosterRegen, builtIds, tickHull } from '../sim/stats';
import { advance as advanceColony, founded, markReached } from '../sim/colony';
import { orbitAt, planetAt } from '../sim/planets';
import type { Planet } from '../data/types';
import {
  clampZoomStep, hintSeen, loadZoomStep, markHintSeen, nowSeconds, saveZoomStep, store, saveLocal,
} from '../state/store';
import { PeerField } from '../net/peers';
import type { Session, SessionInfo } from '../net/session';
import { FieldSocket } from '../net/socket';
import type {
  ColossusInfo, DropOrigin, FragmentSetInfo, HostileHit, PlayerPresence, ProbeHitInfo,
  RockHit, ServerMessage,
} from '../net/protocol';
import { veinAlpha, veinFrameOf } from '../gfx/asteroid';
import {
  BASE_DEBRIS_COUNT,
  BASE_DEBRIS_RING,
  BASE_DEBRIS_TINT,
  BASE_DOCK_TINT,
  BASE_EDGE_TINT,
  BASE_FINAL_RING,
  BASE_HULL_ASPECT,
  BASE_HULL_TINT,
  BASE_IDLE_TINT,
  BASE_SHADOW_TINT,
  baseModule,
  chainBuilds,
  chainLength,
  chainLevel,
  finalTint,
  hullRadius,
  maxChainLength,
} from '../gfx/base';
import {
  ANOMALY_TINT, ROCK_DAMAGE_TINT, ROCK_RICH_TINT, SIGNAL_TINT, hostileTint,
} from '../gfx/palette';
import {
  BASE_SHEET_KEY,
  BOLT_SCALE,
  BOLT_SHEET_KEY,
  DRONE_SCALE,
  PROBE_FRAME,
  ROCK_FRAME,
  FIND_SHEET_KEY,
  ROCK_SHEET_KEY,
  VEIN_FRAME,
  VEIN_INSET,
  VEIN_SHEET_KEY,
  boltFrameOf,
  cacheFrameOf,
  droneLook,
  enemyLook,
  flipOf,
  gearFrameOf,
  hostileRenderScale,
  probeLook,
  shipLook,
  shipRenderScale,
  variantOf,
} from '../gfx/sprites';
import { BACKDROP_FRAGMENT, BACKDROP_SHADER_NAME, NEBULA_ALPHA, layerUV } from '../gfx/backdrop';
import {
  NEBULA_KEY, NEBULA_TILE, STAR_TILE, basePartKey, dropTextureKey, ensurePlanetTextures,
} from '../gfx/textures';
import { PLANET_TEXEL, planetTextureKey } from '../gfx/planet';
import {
  ZONE_BLEND, makeZoneColors, makeZoneWindow, zoneColorsAt, zoneWindowAt,
  type ZoneColors, type ZoneWindow,
} from '../gfx/zonecolor';
import { Effects, type MissileGuide } from './effects';
import { barAlpha, drawHealthBar, hostileHasBar, rockHasBar } from './healthbar';

const MAX_DT = 0.05;
/**
 * Tiefe der Brockenkoerper und der Adern — **zwei** Werte, und das ist der Punkt.
 *
 * Die Adern werden additiv ueberblendet, und ein Wechsel des Blend-Modus bricht
 * den Zeichenstapel. Auf einer gemeinsamen Tiefe kaeme Koerper, Ader, Koerper,
 * Ader — zwei Flushes je Brocken. So kommen erst alle Koerper, dann alle Adern:
 * zwei Draw-Calls fuer das ganze Feld.
 */
const ROCK_BODY_DEPTH = 5;
const ROCK_VEIN_DEPTH = 6;
/**
 * Tiefe der Sonden und der Gegner — dieselbe Ueberlegung eine Ebene weiter.
 *
 * Jede Gegnerart hat ihr **eigenes Blatt** (`ENEMY_SHEETS`), also kostet ein
 * Wechsel zwischen zwei Arten im Stapel einen Texturwechsel. Auf `HOSTILE_DEPTH`
 * kommt deshalb der Rang mal 0,1: gleiche Raenge stehen zusammen, und es bleibt
 * bei einem Bind je sichtbarer Art statt einem je Gegner. Der Abstand zu
 * `PROBE_DEPTH` traegt die halbe Tiefe von fuenf Raengen.
 */
const PROBE_DEPTH = 23;
const HOSTILE_DEPTH = 24;
/**
 * Der Waechter des tiefen Raums steht **ueber** allen Raengen.
 *
 * Er teilt sich seinen Rang mit den uebrigen Kolossen — die Farbe haengt am
 * Rang, nicht an der Art —, aber nicht seine Tiefe: er ist der eine Gegner an
 * einem festen Ort, und er darf nicht hinter einer Schrottdrohne verschwinden,
 * die zufaellig spaeter eingefuegt wurde. Ueber allen fuenf Raengen (24,5) und
 * unter den Begleitdrohnen (28).
 */
const GUARDIAN_DEPTH = 27;
/** Tiefe der Begleitdrohnen — ueber den Gegnern, unter dem Schiff. */
const DRONE_DEPTH = 28;
const AUTOSAVE_SECONDS = 8;

/**
 * Der einmalige Hinweis auf das Feuer des Wächters.
 *
 * Er erklärt das ausbleibende Erz: sein Abschuss gehört keinem Spieler, also
 * gibt es dafür nichts. Ohne den Satz liest sich das als Fehler — man sieht
 * einen Gegner sterben und bekommt nichts. Dass er gezeigt wurde, merkt sich
 * der Browser, nicht der Spielstand (Gate FG-28).
 */
const WARDEN_FIRE_HINT = 'warden-fire';

/**
 * Wie lange der Verladearm für einen Griff braucht.
 *
 * Lang genug, um ihn zu sehen, kurz genug, um niemanden aufzuhalten: der Fund
 * ist zu diesem Zeitpunkt längst gebucht, die Animation hält nichts an. Ein
 * Greifvorgang, der das Spiel anhielte, wäre eine Zwischensequenz — und die
 * entscheidet nichts (`oberflaeche.md`).
 */
const ARM_USE_SECONDS = 1.4;

/** Wie weit die Klaue über den Rumpf hinausfährt, wenn das Ziel weiter weg ist. */
const ARM_MAX_REACH = 46;
const CAM_LEAD = 0.28;
/** Abstand des Kapsel-Pfeils zum Bildrand, in Bildpunkten. */
const COMPASS_MARGIN = 26;
/** Wie lange eine Peilung steht, bevor sie verblasst. */
const BEARING_SECONDS = 12;
/** Farbe des Peilungspfeils — die der Energie, nicht die der Kapsel. */
const BEARING_TINT = 0x5fe6c8;

/** Wie lange ein frisch gebautes Bauteil braucht, um an der Station anzukommen. */
const BASE_RAISE_SECONDS = 1.1;
/** Wie weit es dabei von aussen heranfaehrt, in Weltmass. Ganzzahlig gesetzt. */
const BASE_RAISE_SLIDE = 12;
/** Anteil der Zeit, den es dabei weiss aufblitzt. */
const BASE_RAISE_FLASH = 0.28;
/**
 * Wie nah die Kapsel sein muss, damit die Enthuellung losfaehrt.
 *
 * Gebaut wird ueber das Panel, und das geht von ueberall — wer im Aussenfeld
 * steht, saehe den Effekt sonst nie. Er wartet deshalb, statt ins Leere zu
 * laufen.
 */
const BASE_RAISE_SIGHT = 620;
/** Nach so vielen Sekunden verfaellt die Wartestellung: was laengst steht, erscheint nicht mehr. */
const BASE_ARM_SECONDS = 240;

// Wie schnell die Hintergrundebenen mitziehen. Kleiner heisst ferner.
const NEBULA_PARALLAX = 0.25;
const STAR_FAR_PARALLAX = 0.24;
const STAR_NEAR_PARALLAX = 0.5;

interface SpaceData {
  session: Session;
  info: SessionInfo;
}

/**
 * Wie die Adern eines Erzes aussehen — einmal je Erz gerechnet, nie je Brocken.
 *
 * Ein Sprite traegt nur **einen** Tint und **eine** Deckkraft; beides haengt am
 * Erz und an der Erzklasse und aendert sich waehrend eines Spiels nicht.
 */
interface RockVeinLook {
  frame: number;
  tint: number;
  alpha: number;
  alphaRich: number;
}

export class SpaceScene extends Phaser.Scene {
  static readonly KEY = 'space';

  private session!: Session;
  private world!: World;
  private hostiles!: HostileField;
  private anomalies!: AnomalyField;
  private probes!: ProbeField;
  private ship!: Ship;
  private fx!: Effects;
  private socket: FieldSocket | null = null;

  /** Der gerechnete Hintergrund. Ohne WebGL bleibt er leer. */
  private backdrop: Phaser.GameObjects.Shader | null = null;
  private starsNear!: Phaser.GameObjects.TileSprite;
  private starsFar!: Phaser.GameObjects.TileSprite;
  private nebula!: Phaser.GameObjects.TileSprite;
  private baseGfx!: Phaser.GameObjects.Graphics;
  private anomalyGfx!: Phaser.GameObjects.Graphics;
  private beamGfx!: Phaser.GameObjects.Graphics;
  private overlayGfx!: Phaser.GameObjects.Graphics;
  private healthGfx!: Phaser.GameObjects.Graphics;
  private wallGfx!: Phaser.GameObjects.Graphics;
  private compassGfx!: Phaser.GameObjects.Graphics;
  private shipSprite!: Phaser.GameObjects.Image;
  private sparks!: Phaser.GameObjects.Particles.ParticleEmitter;

  /** Zuletzt gesetzte Schiffstextur — gewechselt nur bei echter Aenderung. */
  private shipKey = '';
  /** Zuletzt gesetzte Drohnenklasse — sie haengt am selben Level wie `shipKey`. */
  private droneKey = '';
  /** Die Bauteile der Basis. Einmal angelegt, danach nur noch geschaltet. */
  private readonly moduleSprites: Phaser.GameObjects.Image[] = [];
  /** Zuletzt gezeichneter Ausbaustand `${era}:${level}` — wie `shipKey`. */
  private baseKey = '';
  /** Zielpuffer der Kettenabfrage. Sie laeuft je Bild (FG-14). */
  private readonly chainBuf: string[] = [];
  /** Das Bauteil, das gerade erscheint. Leer heisst „keines". */
  private raiseId = '';
  /** Wann die Enthuellung scharf gestellt wurde. */
  private raiseArmed = 0;
  /** Wann sie tatsaechlich losgelaufen ist. 0 heisst „scharf, aber die Kapsel ist zu weit weg". */
  private raiseFrom = 0;
  /** Platz des enthuellten Bauteils in seiner Kette — einmal beim Start ermittelt. */
  private raiseIndex = -1;
  /** Je Erz die fertige Toenung: [unbeschaedigt, beschaedigt]. */
  private readonly rockTints = new Map<string, readonly [number, number]>();
  /** Adernbild je Erz: welcher Frame, welche Farbe, wie hell — einmal vorgerechnet. */
  private readonly rockVeins = new Map<string, RockVeinLook>();
  /** Ziel der Zonenfarben-Rechnung. Einmal angelegt, je Bild ueberschrieben. */
  private readonly zoneBuf: ZoneColors = makeZoneColors();
  /** Das Zonenfenster fuer den Hintergrund-Shader — ebenfalls wiederverwendet. */
  private readonly zoneWin: ZoneWindow = makeZoneWindow();
  private readonly world0: [number, number] = [0, 0];
  private readonly worldSize: [number, number] = [0, 0];
  private readonly uvNebula: [number, number, number] = [0, 0, 0];
  private readonly uvStars0: [number, number, number] = [0, 0, 0];
  private readonly uvStars1: [number, number, number] = [0, 0, 0];

  /** Uhr der Brockendrehung, fortgeschrieben in `step`. */
  private elapsed = 0;
  /**
   * Wann die laufende Notabschaltung endet — auf der Szenenuhr.
   *
   * Ein **Zeitpunkt**, keine Restdauer: ein Rest braucht einen Zähler, der ihn
   * bedient, und genau daran ist der Booster-Ablauf zerbrochen. Online setzt
   * ihn der Server über `towedFor` immer wieder nach — die Uhr, die zählt, ist
   * seine, damit solo und online gleich lange dauern.
   */
  private towUntil = 0;
  /** Ohne WebGL gibt es keinen Hintergrund-Shader — dann faellt der Zonenverlauf grob aus. */
  private webgl = true;

  private readonly rockSprites = new Map<string, Phaser.GameObjects.Image>();
  /** Die Adernebene, ein Sprite je sichtbarem Brocken (Depth `ROCK_VEIN_DEPTH`). */
  private readonly veinSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly hostileSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly probeSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly dropSprites: Phaser.GameObjects.Image[] = [];
  /** Zuletzt gesetzte Textur je Drop-Sprite — dasselbe Motiv wie bei den Texten. */
  private readonly dropTextures: string[] = [];
  private readonly missileSprites: Phaser.GameObjects.Image[] = [];
  private readonly droneSprites: Phaser.GameObjects.Image[] = [];
  private readonly textObjects: Phaser.GameObjects.Text[] = [];
  /**
   * Zuletzt gesetzte Farbe und Groesse je Textobjekt.
   *
   * Kein Spielzustand, sondern ein Gedaechtnis des Zeichenpfads (FG-17 bleibt
   * unberuehrt): `setColor` und `setFontSize` bauen in Phaser die ganze
   * Textur neu auf — Canvas fuellen, Schrift ausmessen, zur GPU laden. Sie
   * blind je Frame zu rufen war der eigentliche Ruckelgrund, nicht die Zahl
   * der Zahlen.
   */
  private readonly textColors: string[] = [];
  private readonly textSizes: number[] = [];
  /**
   * Die Mitspieler: der geglaettete Zustand liegt in `net/peers.ts`, hier
   * stehen nur ihre Darstellungsobjekte. Sie werden **je Frame** gesetzt, nicht
   * nur beim Eintreffen einer Nachricht — sonst springen sie im Servertakt.
   */
  private readonly peerField = new PeerField();
  private readonly peerSprites = new Map<string, {
    body: Phaser.GameObjects.Image;
    label: Phaser.GameObjects.Text;
    /** Zuletzt gezeichnetes Level dieses Mitspielers — wie `shipKey` beim eigenen Schiff. */
    lvl: number;
    /** Zuletzt geschriebener Name. `setText` baut die Textur neu auf, und das hier laeuft je Frame. */
    name: string;
  }>();

  private keys!: Record<string, Phaser.Input.Keyboard.Key>;
  private pointerDown = false;
  /** Ziel des Rechtsklick-Schubs. `null`, solange keiner gesetzt ist. */
  private thrustTarget: { x: number; y: number } | null = null;
  /**
   * Der laufende Kurs des Autopiloten — **Oberflächenzustand**, kein
   * Fortschritt (Gate FG-28). Er überlebt kein Neuladen, und das ist richtig so.
   */
  private course: Course | null = null;
  /** Ist der Lichtflug angefordert? Ob er steht, sagt das Schiff. */
  private warpWish = false;
  /** Wiederverwendete Puffer — kein neues Objekt je Frame (Gate FG-14). */
  private readonly steering: Steering = { ax: 0, ay: 0, boost: false, arrived: false };
  private readonly input$: ShipInput = { ax: 0, ay: 0, boost: false, warp: false };
  private readonly sideBuf: string[] = [];
  /**
   * Der Planet, in dessen Umlaufbahn das Schiff steht — `null` sonst.
   *
   * Anzeige, kein Fortschritt: abgehakt wird die Umlaufbahn solo in
   * `stepPlanets`, online vom Server (`planet`).
   */
  private orbit: Planet | null = null;
  /** Die Kugeln im Feld — eine je Planet, angelegt mit dem Heimatsystem. */
  private readonly planetSprites: Phaser.GameObjects.Image[] = [];
  private readonly planetPoint$: Point = { x: 0, y: 0 };
  /** Seit wann die Kolonie solo zuletzt fortgeschrieben wurde — ein Takt, keine Uhr. */
  private colonyTimer = 0;
  private readonly cacheBuf: CacheFind[] = [];
  /**
   * Wie eine Rakete an ihr Ziel kommt — **Gegner vor Brocken**.
   *
   * Die Rangfolge selbst steht in `sim/guided.ts` und ist dort ohne Browser
   * prüfbar; hier stehen nur die beiden Feldabfragen. Einmal angelegt und nicht
   * je Bild: `fx.update` läuft im Takt (Gate FG-14).
   */
  private readonly missileGuide: MissileGuide = {
    find: (x, y, radius) => pickGuidedTarget(
      x, y, radius,
      this.hostiles.inRange(x, y, radius, this.guideHostiles),
      this.world.queryCircle(x, y, radius, this.guideRocks),
    ),
    // „Lebt das noch?" ohne zu fragen, **was** es ist: ein Brocken ist am Leben,
    // wenn die Welt ihn kennt, ein Gegner, wenn seine Hülle steht. Ein Zweig
    // über das ID-Präfix wäre die stille Typunterscheidung, gegen die das
    // schmale Interface gebaut ist.
    alive: (target) => {
      const feind = this.hostiles.byId(target.id);
      return feind ? this.hostiles.alive(feind) : this.world.isAliveId(target.id);
    },
  };

  private readonly gearBuf: string[] = [];
  /**
   * Der laufende Greifvorgang des Verladearms — **reine Ausgabe**.
   *
   * `t` läuft von `ARM_USE_SECONDS` auf 0; `x`/`y` ist das Ziel. Er entscheidet
   * nichts: gebucht hat der Fund, bevor der Arm sich bewegt (solo `findCaches`,
   * online der Server). Eine Animation, die etwas entscheidet, sieht nur, wer
   * sie nicht übersprungen hat — dieselbe Regel wie bei der Heimflug-Sequenz.
   *
   * Er steht als Feld und nicht als Tween, weil seine Uhr in `step` laufen muss:
   * der Zeichenpfad schreibt keinen Zustand (Gate FG-17).
   */
  /** Vor welcher gesperrten Kiste der Spieler gerade steht — gegen Dauermeldung. */
  private blockedAt = '';
  /**
   * Der laufende Takt des Suchsignals — **reine Ausgabe**.
   *
   * Er zählt in `step` herunter, nicht im Zeichenpfad (Gate FG-17), und der
   * Puffer daneben spart ein Objekt je Bild (Gate FG-14).
   */
  private signalT = 0;
  private readonly signal$: CacheSignal = { dist: 0, interval: 0, strength: 0 };

  private armT = 0;
  /** Welches Fundstueck greift — das Bild folgt daraus. */
  private armGear = '';
  /** Welchen Fund er dabei am Haken hat — er faehrt mit ein. */
  private armCache = '';
  private armX = 0;
  private armY = 0;
  private armSprite: Phaser.GameObjects.Image | null = null;

  private readonly guideHostiles: Hostile[] = [];
  private readonly guideRocks: Rock[] = [];
  /**
   * Wohin der Schaden einer Explosion geht.
   *
   * Die **Regel** — Gegner immer, Brocken nur ausserhalb des Gefechts, und mit
   * welchem Anteil — steht in `sim/blast.ts` und ist dort ohne Browser
   * pruefbar. Hier stehen nur die beiden Buchungen. Einmal angelegt und nicht
   * je Einschlag (Gate FG-14); den vollen Schaden und den Kanal reicht
   * `explode` ueber die Felder darunter herein, damit die Senke ein Feld
   * bleiben kann statt je Aufruf zu entstehen.
   */
  private readonly blastSink: BlastSink<Hostile, Rock> = {
    hostile: (hostile, share) => this.damageHostile(
      this.blastAt, hostile, enemyDamage(this.blastDamage * share), this.blastChannel, 0xffb06a,
    ),
    rock: (rock, share) => this.damage(rock, this.blastDamage * share, false, this.blastChannel),
    foeBuf: [],
    rockBuf: [],
  };

  private blastDamage = 0;
  private blastChannel = '';
  private readonly blastAt: Point = { x: 0, y: 0 };
  private readonly cacheSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly gearSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly gearPoint: Point = { x: 0, y: 0 };
  /** Wiederverwendeter Puffer für die Ortsrechnung je Bild (Gate FG-14). */
  private readonly cachePoint: Point = { x: 0, y: 0 };
  private readonly sidePoint$: Point = { x: 0, y: 0 };
  private readonly keyVec = { x: 0, y: 0 };
  private panelOpen = false;
  /** Eigene Spieler-ID aus dem `welcome` — fuer den eigenen Kolossanteil. */
  private playerId = 0;
  /** Ganze Stufen, die der Spieler von der berechneten Zoomstufe abweicht. */
  private zoomStep = loadZoomStep();
  /** Zeigerabstand beim Kneifen. 0 heisst: es wird gerade nicht gekniffen. */
  private pinchDist = 0;
  /** Aenderungsschluessel der Kamera — siehe `applyZoom`. */
  private zoomKey = '';
  private saveTimer = 0;
  private lastZone = -1;
  /**
   * Änderungsschlüssel der Gefechtsmeldung — 0 heißt „kein Gefecht".
   *
   * Ohne ihn ginge sechzigmal pro Sekunde dieselbe Meldung über den Bus.
   */
  private lastCombat = 0;
  /** Zielpuffer der Gefechtsmeldung (Gate FG-14). */
  private readonly engagedBuffer: Hostile[] = [];
  /** Name der Blase, in der das Schiff gerade steht — `null` ausserhalb. */
  private lastAnomaly: string | null = null;
  private warnedEmpty = false;
  private warnedWall = false;
  private colossus: ColossusInfo | null = null;
  /**
   * Die laufende Peilung — **Oberflächenzustand**, kein Fortschritt.
   *
   * Sie gehört nicht in den Spielstand (Gate FG-28) und überlebt bewusst kein
   * Neuladen: eine Peilung ist eine Frage von eben, keine gekaufte Stufe.
   */
  private bearing: BearingHit | null = null;
  private bearingUntil = 0;
  private bearingLabel!: Phaser.GameObjects.Text;
  /** Wiederverwendeter Zielpuffer der Flaechenwirkung (Gate FG-14). */
  private readonly areaBuffer: Rock[] = [];
  /** Zielpuffer der Flaechenwirkung auf Gegner (Gate FG-14). */
  private readonly hostileBuffer: Hostile[] = [];
  private readonly shotSprites: Phaser.GameObjects.Image[] = [];

  constructor() {
    super(SpaceScene.KEY);
  }

  init(data: SpaceData): void {
    this.session = data.session;
  }

  create(): void {
    const seed = balancing().world.defaultSeed;
    // Die Ära des Spielstands bestimmt das Feld — die vier Erzeuger müssen sie
    // **gemeinsam** kennen. Bliebe einer bei Akt I, sähe der Spieler die
    // Brocken der einen Welt und die Gegner der anderen, ohne Fehlermeldung.
    const era = store.save.era;
    store.fieldSeed = seed;
    this.world = new World(seed, store.authoritative, era);
    this.hostiles = new HostileField(seed, store.authoritative, era);
    this.anomalies = new AnomalyField(seed, era);
    this.probes = new ProbeField(seed, era);
    this.ship = new Ship();
    this.resetToDock();
    this.fx = new Effects();

    this.buildLayers();
    this.bindInput();

    this.cameras.main.setRoundPixels(true);
    this.cameras.main.centerOn(this.ship.x, this.ship.y);
    this.applyZoom();
    this.scale.on('resize', () => this.applyZoom());
    // Sensorik weitet das Sichtfeld — aber nur, wenn jemand nachrechnet. Ohne
    // das hier griff ein gekaufter Sensorknoten erst beim naechsten
    // Fenstergroessen-Wechsel oder Levelaufstieg.
    bus.on('state:changed', () => this.applyZoom());

    if (this.session.mode === 'online') this.connect();

    bus.on('panel:close', () => { if (store.authoritative) saveLocal(); });
    // Nach einem Kontowechsel traegt die Adresse einen anderen Token. Ohne
    // neuen Aufbau spielte der Spieler unter dem alten Konto weiter.
    bus.on('account:changed', () => {
      this.socket?.disconnect();
      this.socket = null;
      this.hardReset();
      if (this.session.mode === 'online') this.connect();
    });
  }

  /**
   * Saat und Ära **aller vier** Erzeuger auf einmal setzen.
   *
   * Es gibt vier Feldklassen und damit vier Stellen, an denen eine Ära fehlen
   * könnte. Sie stand vorher dreimal untereinander (`world`, `anomalies`,
   * `probes`) — und die Gegner fehlten in beiden `reset`-Blöcken bereits, ohne
   * dass es jemandem auffiel. Genau das ist der Fehler, den ein gemeinsamer
   * Einstieg unmöglich macht.
   */
  private resetFields(seed: number, era: number, authoritative: boolean): void {
    // Die Oberflaeche liest sie fuer das Fundbuch — sie kennt die Szene nicht.
    store.fieldSeed = seed;
    this.world.reset(seed, authoritative, era);
    this.hostiles.reset(seed, authoritative, era);
    this.anomalies.reset(seed, era);
    this.probes.reset(seed, era);

    // Sofort um das Schiff herum laden. `welcome` und `snapshot` sind zwei
    // aufeinanderfolgende Nachrichten, zwischen denen **kein** Bildschritt
    // liegt — und `reset` hat gerade jede Karte geleert. `applyServerDelta`
    // greift per ID zu und liefert sonst fuer *jede* Abweichung `undefined`:
    // der Aufschluss wirkte bisher nur beim Sektorwechsel, nie beim Verbinden.
    // `dt = 0` ruehrt keine Felduhr an.
    this.world.update(0, this.ship.x, this.ship.y);
    this.hostiles.update(0, this.ship.x, this.ship.y, store.save.built);
    this.anomalies.update(this.ship.x, this.ship.y);
    this.probes.update(0, this.ship.x, this.ship.y);
  }

  // ── Aufbau ─────────────────────────────────────────────────────────────────
  private buildLayers(): void {
    const { width, height } = this.scale;
    // Steht ganz vorn: der Hintergrund entscheidet als **erstes**, ob er
    // gerechnet oder gekachelt wird.
    this.webgl = this.game.renderer.type === Phaser.WEBGL;

    // Mit WebGL traegt **ein** Bildschritt den ganzen Hintergrund: Himmel,
    // Nebel und beide Sternebenen, je Bildpunkt aus seiner Weltposition. Das
    // ersetzt eine Hintergrundfarbe und drei bildschirmfuellende Kachelbilder —
    // vier Fuellungen werden zu einer.
    //
    // Ohne WebGL bleiben die Kachelbilder. Sie zeigen dann weiter den globalen
    // Nebel der Kameraposition, aber mit **gemischtem** Ton statt einem
    // Texturwechsel: die harten Spruenge fallen auch dort weg.
    if (this.webgl) {
      this.backdrop = this.add.shader(
        { name: BACKDROP_SHADER_NAME, fragmentSource: BACKDROP_FRAGMENT },
        0, 0, width, height, [NEBULA_KEY, 'stars:0', 'stars:1'],
      ).setOrigin(0.5).setDepth(-40);
      this.backdrop.setUniform('uNebula', 0);
      this.backdrop.setUniform('uStars0', 1);
      this.backdrop.setUniform('uStars1', 2);
      this.backdrop.setUniform('uBlend', ZONE_BLEND);
    } else {
      this.nebula = this.add.tileSprite(0, 0, width, height, NEBULA_KEY)
        .setOrigin(0).setScrollFactor(0).setDepth(-40).setAlpha(NEBULA_ALPHA);
      this.starsFar = this.add.tileSprite(0, 0, width, height, 'stars:0')
        .setOrigin(0).setScrollFactor(0).setDepth(-30);
      this.starsNear = this.add.tileSprite(0, 0, width, height, 'stars:1')
        .setOrigin(0).setScrollFactor(0).setDepth(-20);
    }

    this.bearingLabel = this.add.text(0, 0, '', {
      fontFamily: '"Lucida Console", monospace', fontSize: '11px', color: '#5fe6c8',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(36).setVisible(false);

    this.baseGfx = this.add.graphics().setDepth(-10);
    // Unter den Brocken (Tiefe 5): die Blase ist Umgebung, kein Objekt.
    this.anomalyGfx = this.add.graphics().setDepth(1);
    this.beamGfx = this.add.graphics().setDepth(20);
    this.overlayGfx = this.add.graphics().setDepth(25);
    // Ueber Brocken (5), Sonden (23) und Gegnern (24), unter den Drohnen (28):
    // der Balken gehoert zum Ziel, soll aber nichts verdecken, was fliegt.
    this.healthGfx = this.add.graphics().setDepth(26);
    this.wallGfx = this.add.graphics().setDepth(-8);
    // Der Pfeil klebt am Bildrand, nicht an der Welt.
    this.compassGfx = this.add.graphics().setScrollFactor(0).setDepth(30);

    this.sparks = this.add.particles(0, 0, 'spark', {
      lifespan: 420,
      speed: { min: 40, max: 150 },
      scale: { start: 0.5, end: 0 },
      quantity: 0,
      emitting: false,
      blendMode: 'ADD',
      // Ohne Deckel: `hitEffect` streut zwei Funken je Brocken und **Frame**.
      // Bei zwei Dutzend gleichzeitig beschossenen Brocken sind das ueber
      // tausend gleichzeitig lebende Partikel — und der Ruckler kommt genau
      // dann, wenn am meisten los ist.
      maxAliveParticles: 260,
    }).setDepth(22);

    // Die Bauteile der Basis: so viele Bilder, wie die laengste Kette lang ist.
    // Sie entstehen **einmal** und werden danach nur sichtbar geschaltet und
    // umtexturiert — der Ausbau bewegt sich in Stufen, nicht je Bild.
    // Tiefe -9: ueber dem Rumpf (-10), weit unter den Brocken (5).
    for (let i = this.moduleSprites.length; i < maxChainLength(); i++) {
      this.moduleSprites.push(this.add.image(0, 0, 'spark').setDepth(-9).setVisible(false));
    }

    const start = shipLook(store.save.ship.level);
    this.shipKey = `${store.save.ship.level}`;
    this.droneKey = this.shipKey;
    this.shipSprite = this.add.image(0, 0, start.key, start.frame)
      .setDepth(30).setScale(shipRenderScale(store.save.ship.level));

    // Erzfarbe und Schadensfaerbung waeren zwei Tints auf demselben Sprite —
    // ein Sprite traegt aber nur einen. Also einmal hier ausmultiplizieren
    // statt in jedem Frame.
    this.rockTints.clear();
    this.rockVeins.clear();
    const erze = resources();
    for (const ore of erze) {
      const base = hexToInt(ore.color);
      this.rockTints.set(ore.id, [base, mulHex(base, ROCK_DAMAGE_TINT)]);
      // Welches Adernbild ein Erz traegt, entscheidet die Balancing-Tabelle und
      // nicht die Szene — hier wird nur einmal nachgeschlagen statt je Brocken.
      // Der Tint ist die **unbeschaedigte** Farbe: additiv aufgelegte Adern
      // sollen den Schadensschleier ueberleben (FG-42).
      this.rockVeins.set(ore.id, {
        frame: veinFrameOf(ore, erze),
        tint: base,
        alpha: veinAlpha(ore, false),
        alphaRich: veinAlpha(ore, true),
      });
    }
  }

  private applyZoom(): void {
    // Der Pixel-Look ist ein Vertrag: **ganzzahlige** Skalierung, kein Glaetten
    // (VIEW-001). Sensorik weitet das Sichtfeld, statt herauszuzoomen — sie
    // senkt den Zoom also nur, wenn dabei eine ganze Zahl herauskommt.
    //
    // Der Spieler verschiebt das Ergebnis um **ganze** Stufen. Ein gebrochener
    // Zoom machte das Bild unscharf statt pixelig; darum ein Versatz auf der
    // fertigen Stufe und keine freie Skalierung wie im Technologiebaum (dort
    // darf sie gebrochen sein — der Vertrag gilt der Welt).
    const sensor = Number(store.stats.values.sensor) || 1;
    const raw = this.scale.height / (480 * sensor);
    const zoom = clamp(Math.round(raw) + this.zoomStep, 1, 4);

    // `state:changed` kommt oft — bei jedem eingesammelten Erzposten. Drei
    // Kacheln neu zu bemassen und die Kamera anzufassen, obwohl sich nichts
    // geaendert hat, waere genau die Sorte Arbeit, die den Massenabbau
    // ruckeln laesst.
    const key = `${zoom}:${this.scale.width}:${this.scale.height}`;
    if (key === this.zoomKey) return;
    this.zoomKey = key;

    this.cameras.main.setZoom(zoom);
    // Der gerechnete Hintergrund misst sich je Bild selbst; nur die
    // Kachelbilder des Rueckfalls muessen nachgezogen werden.
    if (this.backdrop) return;
    this.nebula.setSize(this.scale.width, this.scale.height);
    this.starsFar.setSize(this.scale.width, this.scale.height);
    this.starsNear.setSize(this.scale.width, this.scale.height);
  }

  private bindInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;
    this.keys = keyboard.addKeys('W,A,S,D,UP,LEFT,DOWN,RIGHT,SHIFT') as Record<
      string, Phaser.Input.Keyboard.Key
    >;
    keyboard.on('keydown', (event: KeyboardEvent) => {
      unlockAudio();
      if (this.panelOpen) return;
      // `+`/`-` liegen je nach Belegung auf verschiedenen Tasten; `event.key`
      // trifft alle, auch die des Ziffernblocks.
      if (event.key === '+' || event.key === '=') this.nudgeZoom(1);
      else if (event.key === '-' || event.key === '_') this.nudgeZoom(-1);
      else if (event.key === 'x' || event.key === 'X') this.sendSignal();
      else if (event.key === 'f' || event.key === 'F') this.takeBearing();
      // Lichtflug und Autopilot liegen hier und nicht in `main.ts`: sie sind
      // Steuerung der Welt, nicht des Panels. `ui/keys.ts` dokumentiert sie
      // mit `key: null` — verdrahtet sind sie dort, wo sie wirken (FG-04).
      else if (event.key === 'v' || event.key === 'V') this.toggleWarp();
      else if (event.key === 'p' || event.key === 'P') this.clearCourse('abgebrochen');
    });

    // Ohne das oeffnet der Rechtsklick das Kontextmenue des Browsers, und der
    // Schub kommt nie an.
    this.input.mouse?.disableContextMenu();

    // Zweiter Zeiger fuers Kneifen — ohne ihn gibt es am Handy keinen Zoom.
    this.input.addPointer(1);

    this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
      if (this.panelOpen) return;
      this.nudgeZoom(dy < 0 ? 1 : -1);
    });

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      unlockAudio();
      // Beim Kneifen darf der zweite Finger das Schiff nicht losschicken.
      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        this.pointerDown = false;
        return;
      }
      // Bei offenem Panel bleibt der Zeiger fuer den Technologiebaum: das Panel
      // schirmt den Canvas nicht mehr ab (es laesst den Baum durchscheinen und
      // durchklicken), sonst floege das Schiff bei jedem Klick daneben los.
      if (this.panelOpen) return;
      // Rechtsklick setzt ein **Ziel**, die linke Taste zieht wie bisher.
      if (pointer.rightButtonDown()) this.thrustTarget = { x: pointer.worldX, y: pointer.worldY };
      else this.pointerDown = true;
    });
    this.input.on('pointerup', () => { this.pointerDown = false; });
    this.game.events.on(Phaser.Core.Events.BLUR, () => { this.pointerDown = false; });
    bus.on('panel:state', ({ open }) => {
      this.panelOpen = open;
      if (open) { this.pointerDown = false; this.thrustTarget = null; }
    });
  }

  // ── Netz ───────────────────────────────────────────────────────────────────
  private connect(): void {
    this.socket = new FieldSocket(() => this.session.api.socketUrl(), {
      onMessage: (message) => this.onServerMessage(message),
      onOpen: () => bus.emit('net:status', { online: true, players: 0, field: 'Randfeld' }),
      onClose: () => {
        // Ohne Feld gibt es weder Wochenziel noch Feld-Buff.
        store.clearFleet();
        bus.emit('net:status', { online: false, players: 0, field: 'Randfeld' });
      },
    });
    this.socket.connect();
  }

  private onServerMessage(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        // Die Ära kommt vom **Server**: er hat den Spielstand gelesen und
        // daraus das Feld gewählt. Sie hier aus dem lokalen Stand zu nehmen
        // wäre die zweite Wahrheit — und die verlöre genau nach dem Heimflug.
        this.resetFields(message.field.seed, message.field.era, false);
        this.playerId = message.player.id;
        break;
      case 'snapshot':
        // Erst die liegenden Bruchstuecke, dann die Abweichungen: sonst laeuft
        // die Abweichung eines Stuecks ins Leere, weil es den Brocken noch
        // nicht gibt.
        this.adoptFragments(message.fragments);
        for (const delta of message.deltas) {
          // `false`: ein Bestandsabzug ist kein Treffer. Sonst leuchteten beim
          // Aufschliessen alle Lebensbalken des Sektors auf einmal auf.
          this.world.applyServerDelta(delta.id, delta.hp, delta.respawnIn !== null, 0, false);
        }
        for (const delta of message.probes) {
          this.probes.applyServerDelta(delta.id, delta.hp, delta.respawnIn !== null);
        }
        // Dieselbe `false`-Begruendung wie oben. Und die **Rest**zeit statt der
        // vollen Sperre: der Gegner kann laengst wieder stehen.
        for (const delta of message.hostiles) {
          this.hostiles.applyServerDelta(
            delta.id, delta.hp, delta.respawnIn !== null, delta.respawnIn, false,
          );
        }
        // Der gefuehrte Ort des Waechters — **hart** gesetzt, nicht angeglitten.
        // Der Aufschluss ist genau der Moment, in dem der Client beliebig
        // veraltet sein kann; dorthin zu gleiten hiesse, nach einem Tab-Wechsel
        // sechzehn Sekunden lang an der falschen Stelle zu stehen. `null` heisst
        // „am Saatpunkt" und nicht „fehlt".
        this.hostiles.guardianSnap(message.guardian);
        this.colossus = message.colossus;
        break;
      case 'rocks':
        this.onRockUpdates(message.hits, message.fragments);
        break;
      case 'hostiles':
        this.onHostileUpdates(message.hits);
        break;
      // Nur der Ort. Seine Huelle kommt als Abweichung ueber `hostiles` — zwei
      // Wege fuer denselben Wert waeren zwei Wahrheiten.
      case 'guardian':
        this.hostiles.guardianAt(message.x, message.y);
        break;
      case 'harvest':
        // Buchen tut der Server; hier wird nur **gezeigt**, dass etwas ankommt.
        for (const [res, amount] of Object.entries(message.res)) store.add(res, amount);
        this.showHarvest(message.drops);
        if (message.levels > 0) this.onLevelUp(message.levels);
        bus.emit('state:changed', undefined);
        break;
      case 'self':
        store.save.ship.energy = message.energy;
        store.save.ship.hp = message.hp;
        if (message.towed) this.onTowed();
        // Die Uhr der Notabschaltung ist die des Servers; der Client zieht
        // seine nur nach. Ohne das liefe sie hier eigenständig weiter und
        // wäre bei jedem Latenzsprung eine andere.
        else if (message.towedFor > 0) this.towUntil = this.elapsed + message.towedFor;
        store.save.ship.level = message.level;
        store.save.ship.xp = message.xp;
        for (const [res, amount] of Object.entries(message.res)) store.save.res[res] = amount;
        // Die Kolonie arbeitet beim Server; hier steht nur ihr Lagerstand samt
        // Alter, damit die Anzeige von dort aus fortschreiben kann.
        store.setGoods(message.goods, message.goodsAge);
        break;
      case 'presence':
        this.onPresence(message.players);
        break;
      case 'error':
        bus.emit('toast', { text: message.message, tone: '' });
        break;
      default:
        this.onCoopMessage(message);
        break;
    }
  }

  /** Die kooperativen Ereignisse: Koloss, Signal, Sonden, Flottenauftrag. */
  private onCoopMessage(message: ServerMessage): void {
    switch (message.t) {
      case 'colossus':
        this.colossus = { ...(this.colossus ?? { id: message.event.id, hp: 0 }), ...message.event };
        break;
      case 'colossus_done':
        bus.emit('toast', { text: this.colossusText(message.rewards), tone: 'gold' });
        this.colossus = null;
        sfx.warp();
        break;
      case 'warden_done':
        this.onWardenDown(message.rewards, message.energy);
        break;
      case 'signal':
        // Der eigene Ping kommt als Echo der Sektorgruppe zurueck — ohne
        // Namensschild, das eigene Schiff steht ja dort.
        this.showSignal(message.x, message.y, message.pid === this.playerId ? null : message.name);
        break;
      case 'probes':
        this.onProbeUpdates(message.hits);
        break;
      case 'side_step':
        this.announceSideStep(message.id, message.done, message.total);
        break;
      case 'planet':
        this.onOrbit(message.id);
        break;
      case 'cache_find':
        // Online meldet der Server den Fund; die Beute steht schon im `harvest`.
        this.announceCacheFind(message.id, message.first);
        break;
      case 'probe_log':
        this.onProbeLog(message.id, message.xp);
        break;
      case 'fleet':
        store.setFleet(message.order, message.progress, message.buffEndsIn);
        bus.emit('state:changed', undefined);
        break;
      case 'fleet_done': {
        const order = fleetOrder(message.order);
        bus.emit('toast', {
          text: `Flottenauftrag erfüllt: ${order?.name ?? message.order} — der Feld-Buff läuft!`,
          tone: 'gold',
        });
        sfx.quest();
        break;
      }
      default:
        break;
    }
  }

  /**
   * Die Ausbeute des Servers sichtbar machen.
   *
   * Online bucht der Server; ohne diese Zeilen springt nur der Zaehler im HUD,
   * und der sichtbarste Schritt der Kernschleife („Erz sammeln") fehlt. Die
   * Drops sind darum **reine Anzeige**: sie fliegen, klingen und leuchten, aber
   * sie buchen nichts (`display`).
   */
  private showHarvest(drops: DropOrigin[]): void {
    for (const drop of drops) {
      // Dieselbe Aufteilung wie solo — ein Brocken zerfaellt in bis zu vier
      // Posten, damit ein grosser Fund auch nach einem grossen Fund aussieht.
      const parts = clamp(1 + Math.floor(drop.r / 18), 1, 4);
      for (let i = 0; i < parts; i++) {
        this.fx.spawnDrop(drop.x, drop.y, drop.res, drop.amount / parts, drop.gem, true);
      }
    }
  }

  /** Einmal sagen, dass hier Schluss ist — nicht bei jedem Anstupsen. */
  private warnOnWall(): void {
    const drin = this.ship.wallDepth() > 0.05;
    if (drin === this.warnedWall) return;
    this.warnedWall = drin;
    if (!drin) return;
    bus.emit('toast', { text: 'Alphawand — hier endet der Sektor R17G', tone: '' });
  }

  // ── Gegner ─────────────────────────────────────────────────────────────────
  //
  // Der Laser feuert weiter **selbsttaetig**; Gegner sind Ziele **mit Vorrang
  // vor Brocken**, und der Vorrang steht jetzt in `Ship`, nicht nur in diesem
  // Kommentar. Es gibt keinen Feuerknopf und keine Zielauswahl.
  //
  // Solo bucht die Szene den Schaden selbst, online der Server — dieselbe
  // Weiche wie beim Abbau (`store.authoritative`).

  private stepHostiles(dt: number, distToBase: number, towing: boolean): void {
    this.hostiles.update(dt, this.ship.x, this.ship.y, store.save.built);
    // Die Schuesse sind **immer** zu sehen, auch online: sie behaupten nichts,
    // sie zeigen den Schaden, der ohnehin stetig verrechnet wird.
    const incoming = this.hostiles.step(
      dt, this.ship.x, this.ship.y,
      (hostile, zielX, zielY, speed) => this.fx.spawnHostileShot(
        hostile.x, hostile.y, zielX, zielY, speed,
        hostileTint(enemy(hostile.kindId)?.tier ?? 0), boltFrameOf(hostile.kindId),
      ),
      (opfer) => this.onWardenFelled(opfer),
    );
    this.announceCombat();

    if (!store.authoritative) return;          // online rechnet der Server
    // Während der Notabschaltung wird kein eingehender Schaden verrechnet —
    // sonst reihte sich eine Abschaltung an die nächste, und der Rücklauf
    // begänne bei jedem Tick von vorn.
    if (towing) return;

    const hull = tickHull(store.save.ship.hp, store.stats.values, incoming, distToBase, dt);
    store.save.ship.hp = hull.hp;
    store.count('hullLost', hull.lost);
    if (hull.towed) this.onTowed();
  }

  /**
   * Einen Gegner treffen — der Gegenpart zu `damage()` fuer Brocken.
   *
   * **Zeichnen immer, buchen nur wenn autoritativ.** Online kommt derselbe
   * Treffer als Abweichung vom Server zurueck (`onHostileUpdates`); wer ihn
   * hier zusaetzlich buchte, rechnete ihn doppelt.
   */
  private damageHostile(
    from: { x: number; y: number }, hostile: Hostile, damage: number, channel: string,
    color: number, crit = false,
  ): void {
    this.fx.spawnBeam(from.x, from.y, hostile.x, hostile.y, color, 1.8, 0.08);
    if (!store.authoritative) return;
    store.countBy('dealt', channel, damage);
    // Der Wächter-Zähler auch solo: er ist der eine Koop-Gegner, der
    // ausdrücklich **allein schaffbar** ist. Online füllt ihn der Server
    // (`SessionDelta.add_warden`); ohne diese Zeile stünde nach einem
    // neunzehnminütigen Solo-Kampf „Schaden am Wächter: 0" im Panel — bei
    // `colossusDealt` wäre das „gibt es solo nicht", hier ist es falsch.
    if (hostile.kindId === balancing().guardian.id) store.count('wardenDealt', damage);
    this.showHostileDamage(hostile, damage, crit);
    if (this.hostiles.damage(hostile, damage)) this.onHostileDown(hostile);
  }

  /**
   * Die Schadenszahl ueber einem Gegner.
   *
   * Brocken hatten sie laengst, Gegner nicht — der Kampf fuehlte sich dadurch
   * an wie „es passiert nichts". Gedrosselt wie bei den Brocken: bei voller
   * Feuerrate waere jede einzelne Zahl ein Flimmern.
   */
  private showHostileDamage(hostile: Hostile, damage: number, crit: boolean): void {
    if (damage <= 0) return;
    if (!crit && !chance(0.5)) return;
    this.fx.spawnText(
      hostile.x, hostile.y - hostile.r - 4, fmt(damage),
      crit ? '#fff4a0' : '#ffd2a0', crit ? 13 : 11,
    );
  }

  /**
   * Setzt das Schiff auf den Kapselpunkt — **aus der Tabelle**, nicht getippt.
   *
   * Der Punkt stand vorher dreimal hier und gar nicht auf dem Server. Sobald
   * der Server einer Notabschaltung selbst folgt, müssen beide Seiten denselben
   * Punkt meinen: 170 Einheiten Abstand reichen, damit die Bewegungsprüfung die
   * nächste Standortmeldung still verwirft (`NET-001`).
   */
  private resetToDock(): void {
    const [x, y] = balancing().world.dockPoint;
    this.ship.reset(x, y);
  }

  /**
   * Notabschaltung: das Schiff wird zur Kapsel geschleppt.
   *
   * Das kostet **Zeit**, nie Fortschritt (Gate FG-37). Es gibt keinen Tod, und
   * kein Wert im Spielstand wird dabei kleiner.
   */
  private onTowed(): void {
    this.resetToDock();
    // Sie dauert, was in der Tabelle steht. Vorher war das Abschleppen ein
    // Sprung ohne Dauer: die Hilfe sagte 6 s zu, das Spiel kannte sie nicht —
    // und damit kostete Schaden am Ende gar nichts (FG-37).
    this.towUntil = this.elapsed + balancing().towSeconds;
    if (store.authoritative) store.count('tows');
    this.cameras.main.centerOn(this.ship.x, this.ship.y);
    this.cameras.main.flash(320, 90, 40, 40);
    bus.emit('toast', { text: 'Hülle durch — Notabschaltung, du wurdest zur Kapsel geschleppt', tone: '' });
    sfx.warp();
  }

  private onHostileDown(hostile: Hostile): void {
    sfx.crack();
    this.sparks.emitParticleAt(hostile.x, hostile.y, 14);
    store.count('enemies');
    store.countBy('enemiesByKind', hostile.kindId);

    // Erledigte Gegner geben Energie zurueck — der **aktive** Weg, draussen
    // nachzuladen. Spiegel von `_hit_hostile` auf dem Server.
    const art = enemy(hostile.kindId);
    if (art && art.energy > 0) {
      const max = Number(store.stats.values.energyMax);
      store.save.ship.energy = Math.min(max, store.save.ship.energy + art.energy);
      this.fx.spawnText(hostile.x, hostile.y - 14, `+${fmt(art.energy)} E`, '#7ee0ff');
    }

    // Eine **eigene** Beute ist keine Zonenausbeute: sie wird nicht mit
    // `zone.yield` skaliert, sonst hinge die Menge an einer Zahl, die mit der
    // Zone um Größenordnungen springt — beim Wächter wären aus achtundvierzig
    // Kernen sechsundachtzig Millionen. Wortgleich zu `_kill_hostile`.
    const zone = zonesFor(store.save.era)[hostile.zoneIdx];
    const beute = art?.loot ?? '';
    const ore = beute || zone.ores[zone.ores.length - 1].res;
    const menge = beute
      ? hostile.yieldMul * Number(store.stats.values.yieldMul)
      : zone.yield * hostile.yieldMul * Number(store.stats.values.yieldMul);
    this.fx.spawnDrop(hostile.x, hostile.y, ore, menge, resource(ore)?.gem ?? false);

    const levels = store.addXp(hostile.xp * Number(store.stats.values.xpMul));
    if (levels > 0) this.onLevelUp(levels);
    bus.emit('state:changed', undefined);
  }

  // ── Sonden ─────────────────────────────────────────────────────────────────
  //
  // Erfahrung und Lore, nie Erz. Solo bergt die Szene selbst, online der
  // Server — dieselbe Weiche wie beim Abbau (`store.authoritative`).

  private stepProbes(dt: number): void {
    this.probes.update(dt, this.ship.x, this.ship.y);
    if (!store.authoritative) return;          // online rechnet der Server

    const v = store.stats.values;
    const reichweite = Number(v.laserRange);
    const dps = Number(v.laserDamage) * Number(v.globalDamage) * Number(v.laserRate);
    for (const probe of this.probes.all()) {
      if (!this.probes.alive(probe)) continue;
      const abstand = Math.hypot(probe.x - this.ship.x, probe.y - this.ship.y);
      if (abstand > reichweite + probe.r) continue;
      this.fx.spawnBeam(this.ship.x, this.ship.y, probe.x, probe.y, 0x9fe8c8, 1.6, 0.08);
      if (this.probes.damage(probe, dps * dt)) this.onProbeSalvaged(probe);
      break;                                   // ein Ziel je Frame reicht
    }
  }

  /** Solo: eine Sonde geborgen — Spiegel von `_hit_probe` auf dem Server. */
  private onProbeSalvaged(probe: Probe): void {
    sfx.crack();
    this.sparks.emitParticleAt(probe.x, probe.y, 12);
    store.count('probes');

    const first = !store.save.probeLogs[probe.logId];
    store.save.probeLogs[probe.logId] = true;
    const share = first ? 1 : balancing().probes.repeatShare;
    const xp = probe.xp * share * Number(store.stats.values.xpMul);
    const levels = store.addXp(xp);
    if (levels > 0) this.onLevelUp(levels);

    if (first) this.onProbeLog(probe.logId, xp);
    else bus.emit('toast', { text: `Sonde geborgen — Restdaten, +${fmt(xp)} XP`, tone: '' });
    bus.emit('state:changed', undefined);
  }

  /** Ein geborgenes Daten-Log melden — online kommt es vom Server. */
  private onProbeLog(logId: string, xp: number): void {
    store.save.probeLogs[logId] = true;
    const log = probeLog(logId);
    bus.emit('toast', {
      text: `Daten-Log geborgen: ${log?.title ?? logId} (+${fmt(xp)} XP)`,
      tone: 'gold',
    });
    sfx.quest();
    bus.emit('state:changed', undefined);
  }

  private onProbeUpdates(hits: ProbeHitInfo[]): void {
    for (const hit of hits) {
      const probe = this.probes.applyServerDelta(hit.id, hit.hp, hit.dead);
      if (!probe) continue;
      if (hit.dead) {
        sfx.crack();
        this.sparks.emitParticleAt(probe.x, probe.y, 12);
      }
    }
  }

  /**
   * Solo: der Wächter hat einen Gegner weggeräumt.
   *
   * **Dieselbe Rückmeldung wie online**, wo derselbe Abschuss als
   * `dead=true`-Delta ankommt und in `onHostileUpdates` kracht und funkt. Ohne
   * sie verschwände der Gegner mitten im Bild, und der Spieler hätte keine
   * Möglichkeit zu unterscheiden, ob der Wächter ihn geholt hat, ob ein Sprite
   * verlorenging oder ob ihm gerade Beute weggenommen wurde.
   *
   * **Gebucht wird nichts** — kein Erz, keine Erfahrung, kein Zähler. Genau
   * deshalb geht der Weg nicht über `damage()`: das löst `onHostileDown` aus.
   * Der einmalige Hinweis sagt das, sonst liest sich das ausbleibende Erz als
   * Fehler. Dass er gezeigt wurde, merkt sich der **Browser**, nicht der
   * Spielstand (Gate FG-28).
   */
  private onWardenFelled(hostile: Hostile): void {
    sfx.crack();
    this.sparks.emitParticleAt(hostile.x, hostile.y, 14);
    if (!hintSeen(WARDEN_FIRE_HINT)) {
      markHintSeen(WARDEN_FIRE_HINT);
      bus.emit('toast', {
        text: 'Der Wächter duldet niemanden neben sich — seine Abschüsse geben nichts her',
      });
    }
  }

  private onHostileUpdates(hits: HostileHit[]): void {
    for (const hit of hits) {
      // Online ist der Server die Quelle des Schadens, also entsteht auch die
      // Zahl aus **seiner** Abweichung. Sie im Client nachzurechnen waere die
      // zweite Wahrheit, die FG-32 gerade verhindert.
      //
      // Die Abweichung geht an die Sektorgruppe, also zeigt sie auch fremden
      // Schaden — wessen, steht nicht darin, und ein Feld dafuer waere eine
      // Protokollerweiterung fuer eine Kosmetik. Bei einem gemeinsamen Gegner
      // ist „er nimmt Schaden" ohnehin die Auskunft, auf die es ankommt.
      const vorher = this.hostiles.byId(hit.id)?.hp ?? 0;
      const hostile = this.hostiles.applyServerDelta(hit.id, hit.hp, hit.dead);
      if (!hostile) continue;
      this.showHostileDamage(hostile, vorher - hostile.hp, false);
      if (hit.dead) {
        sfx.crack();
        this.sparks.emitParticleAt(hostile.x, hostile.y, 14);
      }
    }
  }

  /** Zeichnen — liest nur (Gate FG-17). */
  private syncHostiles(): void {
    // Beschnitten wie die Brocken: ohne das zeichnet die Szene alles bis an den
    // Rand der geladenen Kacheln, und beim Rauszoomen wird genau dort eine
    // Kante sichtbar, an der die Gegner schlagartig aufhoeren.
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2 + 90;
    const halfH = cam.height / cam.zoom / 2 + 90;
    const gesehen = new Set<string>();
    for (const hostile of this.hostiles.all()) {
      if (hostile.hp <= 0) continue;
      if (Math.abs(hostile.x - cam.midPoint.x) > halfW) continue;
      if (Math.abs(hostile.y - cam.midPoint.y) > halfH) continue;
      gesehen.add(hostile.id);
      let sprite = this.hostileSprites.get(hostile.id);
      if (!sprite) {
        // Bild, Groesse, Farbe und Tiefe haengen an Art und Rang — beides
        // aendert sich nie. Sie stehen deshalb hier und nicht im Bildtakt.
        const art = enemy(hostile.kindId);
        const look = enemyLook(hostile.kindId, hostile.id);
        sprite = this.add.image(0, 0, look.key, look.frame);
        // Eine Tiefe je Rang statt einer fuer alle: Phaser sortiert danach, und
        // gleiche Arten stehen so zusammen im Zeichenstapel. Bei einer
        // gemeinsamen Tiefe entschiede die Einfuegereihenfolge, und jeder
        // Wechsel zwischen zwei Arten kostete einen Texturwechsel.
        sprite.setDepth(hostile.kindId === balancing().guardian.id
          ? GUARDIAN_DEPTH
          : HOSTILE_DEPTH + (art?.tier ?? 0) * 0.1);
        sprite.setScale(hostileRenderScale(hostile.r, look.frameSize));
        sprite.setTint(hostileTint(art?.tier ?? 0));
        this.hostileSprites.set(hostile.id, sprite);
      }
      sprite.setPosition(hostile.x, hostile.y);
      // Ohne Zuschlag: die Bilder zeigen nach rechts, wie das Schiff. Der alte
      // Viertelkreis stammt von der 16x16-Blob-Textur, die waagerecht lag.
      sprite.setRotation(hostile.angle);
      sprite.setAlpha(0.4 + 0.6 * (hostile.hp / hostile.maxHp));

      if (!hostileHasBar(hostile.kindId)) continue;
      const alpha = barAlpha(this.hostiles.hitAgeOf(hostile));
      if (alpha <= 0) continue;
      drawHealthBar(
        this.healthGfx, hostile.x, hostile.y, hostile.r, hostile.hp / hostile.maxHp, alpha,
      );
    }
    for (const [id, sprite] of this.hostileSprites) {
      if (gesehen.has(id)) continue;
      sprite.destroy();
      this.hostileSprites.delete(id);
    }
  }

  /** Zeichnen — liest nur (Gate FG-17). Sonden blinken, damit man sie findet. */
  private syncProbes(): void {
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2 + 90;
    const halfH = cam.height / cam.zoom / 2 + 90;
    const blink = 0.55 + Math.sin(this.time.now / 320) * 0.35;
    const gesehen = new Set<string>();
    for (const probe of this.probes.all()) {
      if (probe.hp <= 0) continue;
      if (Math.abs(probe.x - cam.midPoint.x) > halfW) continue;
      if (Math.abs(probe.y - cam.midPoint.y) > halfH) continue;
      gesehen.add(probe.id);
      let sprite = this.probeSprites.get(probe.id);
      if (!sprite) {
        const look = probeLook(probe.id);
        sprite = this.add.image(0, 0, look.key, look.frame).setDepth(PROBE_DEPTH);
        sprite.setScale(hostileRenderScale(probe.r, PROBE_FRAME));
        sprite.setTint(0x9fe8c8);
        this.probeSprites.set(probe.id, sprite);
      }
      sprite.setPosition(probe.x, probe.y);
      sprite.setRotation(Math.PI / 4);
      sprite.setAlpha(probe.hp < probe.maxHp ? 1 : blink);
    }
    for (const [id, sprite] of this.probeSprites) {
      if (gesehen.has(id)) continue;
      sprite.destroy();
      this.probeSprites.delete(id);
    }
  }

  /**
   * Die Fundstellen — **nur die im Bild**, und es sind sehr wenige.
   *
   * Anders als Brocken oder Sonden gibt es keine Feldabfrage: die Kisten stehen
   * in der Tabelle, ihre Orte fallen aus der Saat. Die Schleife läuft also über
   * eine Handvoll Einträge, nicht über einen Chunk-Umkreis.
   *
   * Auf der **Karte** erscheint dabei nichts (`sim/mapview.ts` kennt sie nicht):
   * das Suchen ist der Inhalt, und eine Marke nähme ihn weg. Sichtbar ist die
   * Kiste nur, wenn man nah genug dran ist — genau darum ist der Fundradius so
   * großzügig.
   */
  /**
   * Der Verladearm beim Zugriff — **reine Ausgabe**.
   *
   * Er fährt aus dem Rumpf in Richtung des Fundes, hält kurz und fährt zurück.
   * Gezeichnet wird nur, solange die Uhr läuft; sie läuft in `step`, nicht hier
   * (Gate FG-17). Die Methode **weist keinen Zustand zu** — `armSprite` ist der
   * wiederverwendete Knoten, kein Spielzustand.
   *
   * Skaliert wird ganzzahlig nicht: der Arm ist eines der wenigen Dinge, die
   * sich strecken. Er wächst deshalb in der **Länge** (`displayWidth`) und
   * behält seine Pixelhöhe — der Pixel-Look bleibt, weil kein Texel schräg
   * verrechnet wird.
   */
  /**
   * Ein Fund an der Klaue — er hängt daran und fährt mit ein.
   *
   * Beim **Ausfahren** bleibt er liegen: der Arm greift erst zu, wenn er dort
   * ist. Beim **Einfahren** folgt er der Klaue zum Schiff. Ein Fund, der schon
   * mitwandert, während der Arm noch unterwegs ist, sieht aus, als zöge ihn
   * etwas anderes.
   */
  private hookPoint(at: Point, sprite: Phaser.GameObjects.Image): void {
    const anteil = hookProgress(this.armT, ARM_USE_SECONDS);
    sprite.setPosition(
      at.x + (this.ship.x - at.x) * anteil,
      at.y + (this.ship.y - at.y) * anteil,
    );
    // Am Haken pendelt er, statt ruhig zu treiben.
    sprite.setRotation(Math.sin(this.time.now / 140) * 0.22);
  }

  private syncArm(): void {
    if (this.armT <= 0) {
      if (this.armSprite) this.armSprite.setVisible(false);
      return;
    }
    if (!this.armSprite) {
      this.armSprite = this.add
        .image(0, 0, FIND_SHEET_KEY, gearFrameOf(this.armGear))
        .setOrigin(0, 0.5)
        .setDepth(DRONE_DEPTH);
    }

    // Ein Aus- und Einfahren: 0 → 1 → 0 über die Laufzeit.
    const fortschritt = 1 - this.armT / ARM_USE_SECONDS;
    const strecke = Math.sin(fortschritt * Math.PI);

    const dx = this.armX - this.ship.x;
    const dy = this.armY - this.ship.y;
    const winkel = Math.atan2(dy, dx);
    const reichweite = Math.min(ARM_MAX_REACH, Math.hypot(dx, dy));

    this.armSprite.setVisible(true);
    this.armSprite.setPosition(this.ship.x, this.ship.y);
    this.armSprite.setRotation(winkel);
    this.armSprite.displayWidth = Math.max(1, reichweite * strecke);
  }

  /**
   * Die Fundstuecke im Feld — dieselbe Bauform wie `syncCaches`.
   *
   * Auch sie stehen **nicht** auf der Karte: das Suchen ist der Inhalt, und der
   * Fundbuch-Hinweis nennt nur Richtung und Entfernung. Ein geholtes Gerät
   * verschwindet — anders als eine geborgene Kiste, die weiter etwas hergibt.
   */
  private syncGear(): void {
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2 + 90;
    const halfH = cam.height / cam.zoom / 2 + 90;
    const blink = 0.7 + Math.sin(this.time.now / 420) * 0.3;
    const gesehen = new Set<string>();

    for (const gear of balancing().gear) {
      if (gear.minEra > store.save.era) continue;
      if (store.save.gear[gear.id]) continue;          // geholt heisst weg
      gearAt(gear, this.world.seed, store.save.era, this.gearPoint);
      if (Math.abs(this.gearPoint.x - cam.midPoint.x) > halfW) continue;
      if (Math.abs(this.gearPoint.y - cam.midPoint.y) > halfH) continue;
      gesehen.add(gear.id);

      let sprite = this.gearSprites.get(gear.id);
      if (!sprite) {
        sprite = this.add
          .image(0, 0, FIND_SHEET_KEY, gearFrameOf(gear.id))
          .setDepth(PROBE_DEPTH);
        this.gearSprites.set(gear.id, sprite);
      }
      sprite.setPosition(this.gearPoint.x, this.gearPoint.y);
      // Er treibt, wie die Kiste: eine Drehung aus der Uhr, kein gespeicherter
      // Winkel — der Zeichenpfad schreibt nichts (Gate FG-17).
      sprite.setRotation(Math.sin(this.time.now / 2200) * 0.4);
      sprite.setAlpha(blink);
    }

    for (const [id, sprite] of this.gearSprites) {
      if (gesehen.has(id)) continue;
      sprite.destroy();
      this.gearSprites.delete(id);
    }
  }

  private syncCaches(): void {
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2 + 90;
    const halfH = cam.height / cam.zoom / 2 + 90;
    const blink = 0.7 + Math.sin(this.time.now / 420) * 0.3;
    const gesehen = new Set<string>();

    for (const cache of balancing().caches) {
      if (cache.minEra > store.save.era) continue;
      cacheAt(cache, this.world.seed, store.save.era, this.cachePoint);
      if (Math.abs(this.cachePoint.x - cam.midPoint.x) > halfW) continue;
      if (Math.abs(this.cachePoint.y - cam.midPoint.y) > halfH) continue;
      gesehen.add(cache.id);

      let sprite = this.cacheSprites.get(cache.id);
      if (!sprite) {
        sprite = this.add
          .image(0, 0, FIND_SHEET_KEY, cacheFrameOf(cache.id))
          .setDepth(PROBE_DEPTH);
        this.cacheSprites.set(cache.id, sprite);
      }
      // **Am Haken.** Solange der Arm diese Kiste holt, hängt sie an der Klaue
      // und fährt mit ein — das ist der Moment, auf den die Suche hinausläuft.
      // Vorher stand sie unberührt da, während der Arm ins Leere griff.
      if (this.armT > 0 && this.armCache === cache.id) {
        this.hookPoint(this.cachePoint, sprite);
      } else {
        sprite.setPosition(this.cachePoint.x, this.cachePoint.y);
        // Sie treibt: eine langsame Drehung aus der Uhr, kein gespeicherter
        // Winkel — der Zeichenpfad schreibt nichts (Gate FG-17).
        sprite.setRotation(Math.sin(this.time.now / 2600) * 0.35);
      }

      // **Pulsieren erst in Sichtweite** (`CACHE_PULSE_RANGE`). Die mittlere
      // Stufe des Trichters: der Ton sagt „hier irgendwo", das Pulsieren sagt
      // „da", und erst der Verladearm nimmt sie auf. Weiter draußen steht sie
      // ruhig da — sonst wäre das Pulsieren keine Auskunft, sondern Deko.
      const nah = Math.hypot(this.cachePoint.x - this.ship.x, this.cachePoint.y - this.ship.y)
        <= balancing().cache.pulseRange;
      // Eine geborgene Kiste steht offen da und blinkt nicht mehr.
      sprite.setAlpha(store.save.caches[cache.id] ? 0.45 : (nah ? blink : 0.55));
    }

    for (const [id, sprite] of this.cacheSprites) {
      if (gesehen.has(id)) continue;
      sprite.destroy();
      this.cacheSprites.delete(id);
    }
  }

  /**
   * Bruchstuecksaetze des Servers nachrechnen und einhaengen.
   *
   * Online erzeugt der Client sie **nicht** selbst: wie viele Stuecke ein
   * Brocken hinterlaesst, haengt am Splitterbonus dessen, der ihn zerlegt hat.
   * Vorher raet der Client `rock.split` — mit gekaufter Splitterladung waren
   * das zu wenige, und ein Treffer auf ein Bruchstueck lief ins Leere, weil es
   * den Brocken bei ihm gar nicht gab.
   */
  private adoptFragments(sets: FragmentSetInfo[]): void {
    for (const set of sets) {
      this.world.adoptServerFragments(set.parent, set.count, set.expiresIn);
    }
  }

  /**
   * Was nach einem Kolossbrocken in der Meldung steht.
   *
   * Der Anteil kam schon immer mit — angezeigt wurde er nie, die Meldung sagte
   * nur „Beute verteilt". Beim einzigen Inhalt, der Gesellschaft **belohnt**,
   * ist genau der Anteil die Rueckmeldung.
   */
  private colossusText(rewards: Array<{ player: number; share: number }>): string {
    const eigen = rewards.find((entry) => entry.player === this.playerId);
    if (!eigen) return 'Kolossbrocken zerlegt';
    return `Kolossbrocken zerlegt — dein Anteil ${Math.round(eigen.share * 100)} %`;
  }

  /**
   * Was nach einem erledigten Wächter in der Meldung steht.
   *
   * Anders als beim Kolossbrocken hängt hier **sichtbar etwas daran**: an
   * seinem Fall öffnen sich 29 Baumknoten. Der Satz sagt das, weil die Frage
   * „und jetzt?" sonst drei Klicks entfernt im Baum beantwortet wird.
   *
   * Wer nicht beteiligt war, bekommt trotzdem eine Meldung: er hat ihn
   * vielleicht fallen sehen, und ein stummer Moment liest sich als Fehler.
   */
  private wardenText(rewards: Array<{ player: number; share: number }>): string {
    const eigen = rewards.find((entry) => entry.player === this.playerId);
    if (!eigen) return 'Der Wächter des tiefen Raums ist gefallen';
    return `Wächter gefallen — dein Anteil ${Math.round(eigen.share * 100)} %, der tiefe Ast ist offen`;
  }

  /**
   * Der Wächter ist gefallen — Meldung, Klang und die Energie seiner Zellen.
   *
   * **Gebucht wird hier nichts.** Beute, Erfahrung und der `enemiesByKind`-Riegel
   * kommen über `harvest` und `self` vom Server, der sie anteilig auf jeden
   * Beteiligten verteilt hat — auch auf die, die längst offline sind. Die
   * Energie ist die eine Ausnahme, und sie ist keine: sie ist kein Fortschritt,
   * sondern ein laufender Wert, und der nächste `self` überschreibt sie ohnehin.
   */
  private onWardenDown(rewards: Array<{ player: number; share: number }>, energy: number): void {
    const beteiligt = rewards.some((entry) => entry.player === this.playerId);
    bus.emit('toast', { text: this.wardenText(rewards), tone: beteiligt ? 'gold' : '' });
    sfx.warp();
    if (!beteiligt) return;

    // **Der `need_kill`-Riegel als lokale Spiegelung.** Der Server hat ihn über
    // `savegame.mutate` längst gebucht — aber keine Nachricht trägt die
    // Statistik zurück: `self` bringt Energie, Hülle, Level und Vorräte, und
    // `store.adopt` läuft nur bei Sitzungsstart, Login und einem REST-Zug. Ohne
    // diese Zeile stünden die 29 `deep_*`-Knoten für den Rest der Sitzung als
    // gesperrt im Baum, obwohl der Server sie längst freigegeben hat — und die
    // Meldung darüber sagte wortwörtlich das Gegenteil. Dieselbe Bauform wie
    // `store.add` im `harvest`-Zweig: der Client spiegelt, was der Server hat.
    store.countBy('enemiesByKind', balancing().guardian.id);
    if (energy > 0) {
      const max = Number(store.stats.values.energyMax);
      store.save.ship.energy = Math.min(max, store.save.ship.energy + energy);
      this.fx.spawnText(this.ship.x, this.ship.y - 14, `+${fmt(energy)} E`, '#7ee0ff');
    }
    bus.emit('state:changed', undefined);
  }

  private onRockUpdates(hits: RockHit[], sets: FragmentSetInfo[]): void {
    // Erst die neuen Stuecke, dann die Treffer: ein Bruchstueck kann im selben
    // Schritt entstehen und schon Schaden nehmen.
    this.adoptFragments(sets);
    for (const hit of hits) {
      const rock = this.world.applyServerDelta(hit.id, hit.hp, hit.dead, 0);
      if (!rock) continue;
      if (hit.dead) this.onRockDestroyed(rock, false);
      else this.hitEffect(rock);
    }
  }

  // ── Schleife ───────────────────────────────────────────────────────────────
  override update(_time: number, delta: number): void {
    const dt = Math.min(MAX_DT, delta / 1000);
    this.updatePinch();
    this.step(dt);
    this.syncView(dt);
  }

  /**
   * Zoomen mit zwei Fingern.
   *
   * Ein Finger kennt kein Zeigen und kein Mausrad — ohne das hier gaebe es am
   * Handy keinen Zoom. Die Auswertung laeuft ueber den **Abstand** der beiden
   * Zeiger und schaltet erst bei einem Viertel Aenderung eine ganze Stufe: der
   * Weltzoom ist ganzzahlig, ein stufenloses Kneifen haette nichts anzuzeigen.
   */
  private updatePinch(): void {
    if (this.panelOpen || !this.input.pointer1.isDown || !this.input.pointer2.isDown) {
      this.pinchDist = 0;
      return;
    }
    const dist = Phaser.Math.Distance.BetweenPoints(this.input.pointer1, this.input.pointer2);
    if (this.pinchDist <= 0) { this.pinchDist = dist; return; }
    const factor = dist / this.pinchDist;
    if (factor > 1.25) { this.nudgeZoom(1); this.pinchDist = dist; }
    else if (factor < 0.8) { this.nudgeZoom(-1); this.pinchDist = dist; }
  }

  /**
   * Ein frisch gebautes Bauteil sichtbar erscheinen lassen.
   *
   * Wird aus der Verdrahtung gerufen, nachdem der Zug gebucht ist — der Effekt
   * gehoert zur **Handlung**, nicht zum Laden. Steigt `built` auf einem anderen
   * Weg (Reconnect, Serverstand), steht das Bauteil im naechsten Bild einfach
   * da; das entscheidet `syncModules` allein aus dem Spielstand.
   */
  raiseModule(buildId: string): void {
    if (!baseModule(buildId)) return;
    this.raiseId = buildId;
    this.raiseArmed = this.elapsed;
    this.raiseFrom = 0;
    this.raiseIndex = -1;
  }

  /**
   * Die Uhr der Enthuellung — im Zustandspfad, nicht im Zeichenpfad (FG-17).
   *
   * Sie wartet, bis die Kapsel in der Naehe ist, und verfaellt danach: ein
   * Bauteil, das seit einer Viertelstunde steht, erscheint nicht mehr.
   */
  private stepRaise(): void {
    if (!this.raiseId) return;
    if (this.raiseFrom > 0) {
      if (this.elapsed > this.raiseFrom + BASE_RAISE_SECONDS) this.raiseId = '';
      return;
    }
    if (this.elapsed - this.raiseArmed > BASE_ARM_SECONDS) { this.raiseId = ''; return; }
    if (this.ship.distToBase > BASE_RAISE_SIGHT) return;

    const modul = baseModule(this.raiseId);
    const index = chainBuilds(store.save.era, this.chainBuf).indexOf(this.raiseId);
    if (!modul || index < 0) { this.raiseId = ''; return; }
    this.raiseIndex = index;
    this.raiseFrom = this.elapsed;
    this.fx.spawnRing(modul.x, modul.y, 90, modul.tint, 0.55);
    this.sparks.emitParticleAt(modul.x, modul.y, 12);
    sfx.ping();
  }

  /** Den Zoomversatz um eine Stufe verschieben und merken. */
  private nudgeZoom(delta: number): void {
    const next = clampZoomStep(this.zoomStep + delta);
    if (next === this.zoomStep) return;
    this.zoomStep = next;
    saveZoomStep(next);
    this.applyZoom();
  }

  /** Alles, was den Zustand veraendert. Zeichnen passiert danach und nur lesend. */
  private step(dt: number): void {
    store.count('playtime', dt);

    // Die Uhr der Brockendrehung. Sie gehoert hierher und nicht in `syncRocks`:
    // was sich dreht, ist Zustand, und der Zeichenpfad schreibt keinen (FG-17).
    // `rock.rot` selbst bleibt unangetastet — der Winkel ist eine Rechnung.
    this.elapsed += dt;

    // Die Notabschaltung kostet Zeit: für ihre Dauer nimmt das Schiff keine
    // Eingabe an und feuert nicht. Der Server rechnet denselben Schritt.
    const towing = this.elapsed < this.towUntil;
    bus.emit('tow', { left: towing ? this.towUntil - this.elapsed : 0 });

    this.stepRaise();
    if (this.armT > 0) this.armT = Math.max(0, this.armT - dt);
    this.stepSignal(dt);

    const energyBefore = store.save.ship.energy;
    const fromX = this.ship.x;
    const fromY = this.ship.y;

    // Die Blasen um das Schiff nachladen und die Wirkung am Ort bestimmen —
    // vor dem Schiffsschritt, denn Drag und Feuerkosten haengen daran.
    this.anomalies.update(this.ship.x, this.ship.y);
    const anomaly = this.anomalies.effectAt(this.ship.x, this.ship.y);

    const energy = this.ship.update(dt, this.readInput(), {
      world: this.world,
      stats: store.stats,
      energy: store.save.ship.energy,
      regen: boosterRegen(store.save, nowSeconds()),
      ignoredOres: store.save.ignoredOres,
      wishOre: store.save.wishOre,
      anomaly,
      hostiles: this.hostiles,
      frozen: towing,
      hooks: {
        onFire: (channel, shots) => store.countBy('shots', channel, shots),
        onLaser: (from, rock, damage, crit) => {
          this.fx.spawnBeam(from.x, from.y, rock.x, rock.y, crit ? 0xfff4a0 : 0x6fe8ff, crit ? 2.6 : 1.6);
          if (crit) store.count('crits');
          this.damage(rock, damage, crit, 'laser');
        },
        onChain: (from, to, damage, crit) => {
          this.fx.spawnBeam(from.x, from.y, to.x, to.y, crit ? 0xfff4a0 : 0x6fe8ff, 1.2, 0.1);
          this.damage(to, damage, false, 'bounce');
        },
        onChainHostile: (from, hostile, damage) => {
          this.damageHostile(from, hostile, damage, 'bounce', 0x6fe8ff);
        },
        onMissile: (x, y, angle, damage, splash) => this.fx.spawnMissile(x, y, angle, damage, splash),
        onNova: (x, y, radius, damage) => {
          this.fx.spawnRing(x, y, radius, 0x7ee0ff, 0.55);
          this.explode(x, y, damage, radius, 'nova');
        },
        onDrone: (drone, rock, damage) => {
          this.fx.spawnBeam(drone.x, drone.y, rock.x, rock.y, 0xa8ff8a, 1.2, 0.08);
          this.damage(rock, damage, false, 'drone');
        },
        onHostileLaser: (from, hostile, damage, crit) => {
          if (crit) store.count('crits');
          this.damageHostile(from, hostile, damage, 'laser', crit ? 0xfff4a0 : 0xff9a6a, crit);
        },
        onHostileDrone: (drone, hostile, damage) => {
          this.damageHostile(drone, hostile, damage, 'drone', 0xa8ff8a);
        },
      },
    });
    store.save.ship.energy = energy;
    store.count('energySpent', Math.max(0, energyBefore - energy));
    store.count('distance', Math.hypot(this.ship.x - fromX, this.ship.y - fromY));
    if (this.ship.docked) store.count('timeDocked', dt);
    else if (this.ship.empty) store.count('timeEmpty', dt);

    this.world.update(dt, this.ship.x, this.ship.y);
    this.fx.update(
      dt, this.ship,
      Number(store.stats.values.magnetRange),
      Number(store.stats.values.magnetSpeed),
      (res, amount, gem, display) => this.collect(res, amount, gem, display),
      (x, y, damage, radius) => this.explode(x, y, damage, radius, 'missile'),
      this.missileGuide,
    );

    const dist = this.ship.distToBase;
    store.count('deepest', dist);
    this.stepHostiles(dt, dist, towing);
    this.stepProbes(dt);
    // Die Mitspieler zwischen zwei Meldungen fortschreiben. Hier und nicht im
    // Zeichenpfad: der Zustand haengt an der Zeit, nicht an der Bildrate.
    this.peerField.step(dt);
    store.refreshBoosters();
    store.tickFleet(dt);

    this.stepSideQuests();
    this.stepPlanets();
    this.stepColony(dt);
    this.searchCaches();
    this.announceZone(dist);
    this.announceAnomaly();
    this.warnOnEmpty();
    this.warnOnWall();

    this.socket?.update({
      x: this.ship.x, y: this.ship.y,
      vx: this.ship.vx, vy: this.ship.vy,
      angle: this.ship.angle, thrust: this.ship.thrust,
    });

    if (store.authoritative) {
      this.saveTimer += dt;
      if (this.saveTimer > AUTOSAVE_SECONDS) { this.saveTimer = 0; saveLocal(); }
    }
  }

  /**
   * Offene Nebenauftrags-Schritte am Schiffsort abhaken — **nur solo**.
   *
   * Online rechnet der Server denselben Schritt (`_walk_side_quests`) und
   * meldet ihn persönlich zurück; hier selbst zu buchen wäre die zweite
   * Wahrheit, gegen die `store.authoritative` steht.
   */
  private stepSideQuests(): void {
    if (!store.authoritative) return;
    const bewegt = advanceSide(
      store.save, this.ship.x, this.ship.y,
      this.world.seed, builtIds(store.save.built), this.sideBuf,
    );
    for (const id of bewegt) this.announceSideStep(id, store.save.sideSteps[id]);
  }

  /**
   * Die Umlaufbahn der Planeten (Akt III) — welcher gerade unter dem Schiff
   * liegt, und **solo** das Abhaken.
   *
   * Online rechnet der Server dieselbe Frage im Bergbau-Tick und meldet sie
   * persönlich (`planet`). Es bleibt bei drei Client-Nachrichten (FG-32): der
   * Ort ist die eine Sache, die der Client ohnehin meldet.
   */
  private stepPlanets(): void {
    if (store.save.era < balancing().colony.minEra) {
      this.orbit = null;
      return;
    }
    this.orbit = orbitAt(this.ship.x, this.ship.y, this.world.seed, store.save.era);
    if (!store.authoritative || !this.orbit) return;
    for (const id of markReached(store.save, this.ship.x, this.ship.y, this.world.seed)) this.onOrbit(id);
  }

  /**
   * Eine Umlaufbahn ist zum ersten Mal erreicht.
   *
   * Die Marke spiegelt online nur, was der Server schon gebucht hat — wie
   * beim Nebenauftrag. Ohne die Meldung stünde der Landeknopf still frei, und
   * niemand wüsste, dass er es tut.
   */
  private onOrbit(id: string): void {
    const planet = planetById(id);
    if (!planet) return;
    store.save.colony.reached[id] = true;
    sfx.upgrade();
    bus.emit('toast', {
      text: `Umlaufbahn von ${planet.name} erreicht — Landung im Reiter „Kolonie“ (C)`,
      tone: 'gold',
    });
    bus.emit('state:changed', undefined);
  }

  /**
   * Die Kolonie solo fortschreiben — einmal je Sekunde, nicht je Bild.
   *
   * Online tut es der Server beim Spülen; hier zu rechnen wäre die zweite
   * Wahrheit. Die Uhr ist die Wanduhr, gedeckelt in `advance`: wer die Welt
   * anhält, bekommt danach höchstens eine Lücke gutgeschrieben, nie die
   * ganze Pause (`COLONY-001`).
   */
  private stepColony(dt: number): void {
    if (!store.authoritative || !founded(store.save.colony)) return;
    this.colonyTimer += dt;
    if (this.colonyTimer < 1) return;
    this.colonyTimer = 0;
    advanceColony(store.save, nowSeconds());
  }

  /** Der Planet unter dem Schiff — das HUD liest ihn, es schreibt ihn nicht. */
  orbitPlanet(): Planet | null {
    return this.orbit;
  }

  /** Kurs auf einen Planeten. Der Ort folgt aus der Weltsaat, wie beim Nebenauftrag. */
  courseToPlanet(id: string): void {
    const planet = planetById(id);
    if (!planet) return;
    if (store.save.era < balancing().colony.minEra) {
      bus.emit('toast', { text: 'Die Planeten liegen im Heimatsystem.', tone: 'bad' });
      return;
    }
    planetAt(id, this.world.seed, store.save.era, this.planetPoint$);
    this.setCourse(this.planetPoint$.x, this.planetPoint$.y, planet.name);
  }

  /**
   * Das Suchsignal einer Kiste in der Nähe — **Nähe, nie Richtung**.
   *
   * Der Takt wird schneller, je näher man ist; er kommt aus `sim/caches.ts` und
   * wird hier nur abgespielt. Zwei Merkmale, nicht eines: ein Ton **und** ein
   * Ring am Schiff. Wer stumm spielt, bekäme sonst gar nichts — und ohne das
   * Signal ist das Suchen keine Suche (`oberflaeche.md`).
   *
   * Der Ring trägt dabei so wenig Richtung wie der Ton: er geht vom **Schiff**
   * aus, nicht von der Kiste. Ein Zeiger nähme genau das weg, wofür die Funde
   * nicht auf der Karte stehen.
   */
  private stepSignal(dt: number): void {
    if (!cacheSignal(store.save, this.ship.x, this.ship.y, this.world.seed, this.signal$)) {
      // Außer Hörweite: der Takt steht auf null, damit der erste Ton sofort
      // kommt, wenn man hineinfliegt — und nicht bis zu 1,5 s später.
      this.signalT = 0;
      return;
    }
    this.signalT -= dt;
    if (this.signalT > 0) return;

    this.signalT = this.signal$.interval;
    sfx.ping();
    // Je näher, desto weiter und heller der Ring — dieselbe Information wie im
    // Takt, damit beide Merkmale dasselbe sagen.
    this.fx.spawnRing(
      this.ship.x, this.ship.y,
      26 + 30 * this.signal$.strength,
      SIGNAL_TINT,
      0.35 + 0.25 * this.signal$.strength,
    );
  }

  /**
   * Fundstellen am Schiffsort bergen — **nur solo**.
   *
   * Online rechnet der Server denselben Fund (`_search_caches`) und meldet ihn
   * persönlich zurück; die Beute kommt dort im `harvest`. Hier selbst zu buchen
   * wäre die zweite Wahrheit, gegen die `store.authoritative` steht.
   */
  private searchCaches(): void {
    // **Der Riegel wird gemeldet, auch online.** Der Server lässt den Fund
    // einfach aus; ohne diese Rechnung stünde der Spieler vor einem blinkenden
    // Werkzeugschrank, bei dem nichts passiert, und suchte den Fehler bei sich.
    // Beide Seiten rechnen dieselbe Antwort aus denselben Daten — es braucht
    // keine Nachricht dafür (dieselbe Bauform wie der Gefechtschip).
    this.warnBlockedCache();
    if (!store.authoritative) return;
    // **Zuerst die Fundstücke** — wortgleich zur Reihenfolge im Server-Tick: wer
    // im selben Schritt über Arm und Schrank fällt, soll den Schrank mitnehmen
    // und nicht eine Runde später.
    for (const id of findGear(
      store.save, this.ship.x, this.ship.y, this.world.seed, this.gearBuf,
    )) {
      this.announceGearFind(id);
    }
    for (const fund of findCaches(
      store.save, this.ship.x, this.ship.y, this.world.seed, this.cacheBuf,
    )) {
      // Solo bucht der Client: `findCaches` setzt nur die Marke und sagt, was
      // fällig ist — genau, damit online derselbe Aufruf **nichts** bucht.
      for (const [id, amount] of Object.entries(fund.res)) store.add(id, amount);
      if (fund.first) store.count('caches');
      this.announceCacheFind(fund.id, fund.first);
    }
  }

  /**
   * Welches Fundstück diesen Fund hereinholt — oder `''`, wenn keines da ist.
   *
   * Ein Fund, der ein bestimmtes verlangt, bekommt genau dieses; alle anderen
   * das erste, das der Spieler hat. Ohne Fundstück bleibt es bei der Hand — und
   * das muss so bleiben, sonst ließe sich die erste Kiste nicht aufnehmen, und
   * ohne sie fände niemand den Arm.
   */
  private armFor(cache: Cache): string {
    if (cache.needsGear) return cache.needsGear;
    for (const gear of balancing().gear) {
      if (store.save.gear[gear.id]) return gear.id;
    }
    return '';
  }

  /**
   * „Das kriegst du so nicht auf" — einmal je Anflug, nicht je Bild.
   *
   * Die Sperre hängt am **Verlassen** des Fundradius und nicht an einer Frist:
   * eine Frist meldete sich beim Kreisen erneut, ein Zähler nie wieder. So sagt
   * sie es einmal, wenn man ankommt, und wieder, wenn man es noch einmal
   * versucht.
   */
  private warnBlockedCache(): void {
    const treffer = blockedCacheAt(store.save, this.ship.x, this.ship.y, this.world.seed);
    if (!treffer) {
      this.blockedAt = '';
      return;
    }
    if (this.blockedAt === treffer.cache.id) return;
    this.blockedAt = treffer.cache.id;

    const gear = gearById(treffer.gearId);
    bus.emit('toast', {
      text: `${treffer.cache.name} ist zu groß, um sie von Hand zu nehmen — `
        + `dafür brauchst du ${gear ? gear.name : 'den Verladearm'}.`,
      tone: 'bad',
    });
  }

  /**
   * Die Meldung eines aufgenommenen Fundstücks — solo wie online dieselbe.
   *
   * Sie sagt, **was es kann**, nicht nur dass es da ist: ein Gerät ohne Wirkung
   * im Text ist ein Gegenstand im Inventar, den niemand einordnet.
   */
  announceGearFind(id: string): void {
    const gear = gearById(id);
    if (!gear) return;
    store.save.gear[id] = true;         // online setzt der Server sie; idempotent
    bus.emit('toast', {
      text: `${gear.name} aufgenommen! Schwere Funde lassen sich jetzt bergen.`,
      tone: 'gold',
    });
    sfx.quest();
  }

  /**
   * Die Meldung eines Fundes — solo wie online dieselbe.
   *
   * Sie nennt den **nächsten Hinweis**, und der wird aus dem Ort der nächsten
   * Kiste gerechnet, nicht aus einer Nachricht: der Server schickt nur die ID.
   * Ein Text vom Draht wäre die zweite Wahrheit neben dem Ort.
   */
  announceCacheFind(id: string, first: boolean): void {
    const cache = cacheById(id);
    if (!cache) return;
    // Online setzt der Server die Marke; hier steht sie schon. Beides schadet
    // nicht — die Marke ist idempotent, und der offene Hinweis folgt aus ihr.
    if (first) store.save.caches[id] = true;

    // **Der Arm holt sie herein — sichtbar, am Haken.** Er greift bei *jedem*
    // Fund, sobald man ihn hat, nicht nur bei den schweren: er ist das Werkzeug
    // zum Aufnehmen, und eine Kiste, die daneben von selbst verschwindet, macht
    // ihn zur Zierde.
    //
    // Ohne Arm geht es weiter von Hand. Das ist keine Ausnahme, sondern die
    // Bedingung dafür, dass die Kette überhaupt anläuft: der Arm liegt selbst
    // erst hinter der zweiten Kiste (`GEAR`, `band`).
    const arm = this.armFor(cache);
    if (arm) {
      cacheAt(cache, this.world.seed, store.save.era, this.cachePoint);
      this.armX = this.cachePoint.x;
      this.armY = this.cachePoint.y;
      this.armGear = arm;
      this.armCache = cache.id;
      this.armT = ARM_USE_SECONDS;
      sfx.upgrade();
    }

    const naechste = openCache(store.save);
    const hinweis = naechste
      ? ` Darin liegt eine Peilnotiz: ${cacheHint(naechste, this.world.seed, store.save.era)}.`
      : ' Es ist die letzte — die Spur endet hier.';
    bus.emit('toast', {
      text: first
        ? `${cache.name} geborgen!${hinweis}`
        : `${cache.name} — schon durchsucht, viel ist nicht mehr drin.`,
      tone: first ? 'gold' : 'good',
    });
    sfx.quest();
    bus.emit('state:changed', undefined);
  }

  /**
   * Die Meldung eines erreichten Schritts — solo wie online dieselbe.
   *
   * `total` kommt online vom Server mit. Es wird **gelesen**, nicht geraten:
   * hat der Server eine neuere Tabelle als dieser Client, ist seine Kette die
   * richtige, und die Anzeige „3/5" wäre sonst „3/4".
   */
  announceSideStep(id: string, done: number, total?: number): void {
    const quest = sideQuest(id);
    if (!quest) return;
    const laenge = Math.max(quest.steps.length, total ?? 0);
    store.save.sideSteps[id] = Math.min(quest.steps.length, done);
    store.count('sideSteps');
    const fertig = done >= laenge;
    bus.emit('toast', {
      text: fertig
        ? `${quest.name} erfüllt — die Belohnung wartet im Auftragspanel`
        : `${quest.name}: ${done}/${laenge} — ${quest.steps[done]?.label ?? ''}`,
      tone: fertig ? 'gold' : 'good',
    });
    sfx.quest();
    // Der erledigte Schritt war das Kursziel: der nächste steht woanders.
    if (this.course?.label.startsWith(quest.name)) this.clearCourse('erledigt');
    bus.emit('state:changed', undefined);
  }

  /**
   * Die drei Steuerwege — Tastatur, gehaltene Maustaste, Rechtsklick-Ziel.
   *
   * Sie ergaenzen sich und ersetzen sich nicht: die Tastatur hat Vorrang und
   * bricht einen laufenden Schub ab, weil sonst niemand mehr anhalten koennte.
   */
  private readInput(): ShipInput {
    const k = this.keys;
    const tasten = this.readKeys();
    let ax = tasten.x;
    let ay = tasten.y;
    // Eine Hand am Steuer bricht **beides** ab: den Schub und den Autopiloten.
    // Ein Kurs, den man nicht durch Gegensteuern loswird, ist eine Falle.
    if (ax || ay) { this.thrustTarget = null; this.clearCourse('manuell'); }

    if (!ax && !ay && this.pointerDown) {
      const pointer = this.input.activePointer;
      const dx = pointer.worldX - this.ship.x;
      const dy = pointer.worldY - this.ship.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 14) { ax = dx / dist; ay = dy / dist; }
    }

    if (!ax && !ay && this.thrustTarget) {
      const dx = this.thrustTarget.x - this.ship.x;
      const dy = this.thrustTarget.y - this.ship.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 20) { ax = dx / dist; ay = dy / dist; }
      else this.thrustTarget = null;            // angekommen
    }

    this.input$.ax = ax;
    this.input$.ay = ay;
    this.input$.boost = Boolean(k?.SHIFT.isDown);
    this.input$.warp = this.warpWish;
    // Der Autopilot ist die **letzte** Quelle: jede Hand am Steuer geht vor.
    if (!ax && !ay) this.addCourseInput(this.input$);
    return this.input$;
  }

  /** Die Richtungstasten als Vektor — in einen wiederverwendeten Puffer. */
  private readKeys(): { x: number; y: number } {
    const k = this.keys;
    const out = this.keyVec;
    out.x = 0;
    out.y = 0;
    if (!k) return out;
    if (k.A.isDown || k.LEFT.isDown) out.x -= 1;
    if (k.D.isDown || k.RIGHT.isDown) out.x += 1;
    if (k.W.isDown || k.UP.isDown) out.y -= 1;
    if (k.S.isDown || k.DOWN.isDown) out.y += 1;
    return out;
  }

  /**
   * Den Kurs in die Eingabe legen — dieselbe `ShipInput` wie von Hand.
   *
   * Er erzeugt Richtung und Boost, sonst nichts (`sim/navigation.ts`). Damit
   * gilt für ihn jede Regel des Schiffs unverändert, und der Server merkt von
   * ihm nichts: der Client meldet weiter nur seinen Ort (Gate FG-32).
   */
  private addCourseInput(input: ShipInput): void {
    if (!this.course) return;
    steerTo(this.ship.x, this.ship.y, this.ship.vx, this.ship.vy,
      this.course, store.stats, this.steering);
    input.ax = this.steering.ax;
    input.ay = this.steering.ay;
    input.boost = input.boost || this.steering.boost;
    if (this.steering.arrived) this.clearCourse('angekommen');
  }

  // ── Kurs, Autopilot und Lichtflug ──────────────────────────────────────────

  /**
   * Einen Kurs setzen — aus der Karte oder von einem Auftragsmarker.
   *
   * Er ist **Oberflächenzustand**, kein Fortschritt: er gehört weder in den
   * Spielstand noch in den lokalen Zwischenspeicher (Gate FG-28). Wer neu lädt,
   * fliegt selbst.
   */
  setCourse(x: number, y: number, label: string): boolean {
    // Der Grund kommt aus **derselben** Auswertung wie die Sperre. Vorher stand
    // hier ein zweites `store.stats.unlocks.autopilot` — und damit die zweite
    // Regelquelle, die irgendwann etwas anderes sagt als die Wirkung.
    const grund = courseReason(
      this.ship.x, this.ship.y, x, y, store.stats, builtIds(store.save.built),
    );
    if (grund) {
      bus.emit('toast', { text: grund, tone: 'bad' });
      return false;
    }
    this.course = { x, y, label };
    this.thrustTarget = null;
    bus.emit('toast', { text: `Kurs gesetzt: ${label}`, tone: 'good' });
    return true;
  }

  /** Den Kurs beenden. `grund` erscheint nur, wenn einer lief. */
  clearCourse(grund: string): void {
    if (!this.course) return;
    const ziel = this.course.label;
    this.course = null;
    if (grund === 'angekommen') bus.emit('toast', { text: `Angekommen: ${ziel}`, tone: 'good' });
  }

  /** Der laufende Kurs — die Oberfläche liest ihn, sie schreibt ihn nicht. */
  activeCourse(): Course | null {
    return this.course;
  }

  /**
   * Kurs auf den nächsten offenen Schritt eines Nebenauftrags.
   *
   * Der Ort wird **hier** gerechnet und nicht im Panel: `ui` und `scenes` sind
   * Geschwister und kennen sich nicht (Gate FG-04), und die Weltsaat liegt bei
   * der Szene. Das Panel bittet über den Bus, die Szene antwortet mit einem
   * Kurs.
   */
  courseToSideStep(questId: string): void {
    const quest = sideQuest(questId);
    if (!quest) return;
    const done = Math.min(quest.steps.length - 1, store.save.sideSteps[questId] ?? 0);
    stepPoint(quest, done, this.world.seed, store.save.era, this.sidePoint$);
    this.setCourse(this.sidePoint$.x, this.sidePoint$.y, sideQuestLabel(quest, done));
  }

  /**
   * Den Lichtflug umschalten.
   *
   * Er ist ein **Wunsch**: ob das Feld steht, entscheidet das Schiff (Anlauf,
   * Energie, Gefecht). Deshalb steht hier nur ein Schalter und keine Prüfung —
   * eine zweite Prüfung wäre eine zweite Regelquelle.
   */
  toggleWarp(): void {
    if (!store.stats.unlocks.warp) {
      bus.emit('toast', { text: 'Dafür fehlt die Sprungspule.', tone: 'bad' });
      return;
    }
    this.warpWish = !this.warpWish;
    if (this.warpWish) sfx.upgrade();
  }

  /** Zustand des Sprungfelds für das HUD — nichts davon entscheidet etwas. */
  warpState(): { wish: boolean; on: boolean; progress: number } {
    return {
      wish: this.warpWish,
      on: this.ship.warping,
      progress: warpProgress(this.ship.warp, store.stats),
    };
  }

  // ── Abbau (nur im Solo-Modus autoritativ) ──────────────────────────────────
  private damage(rock: Rock, amount: number, crit: boolean, channel: string): void {
    if (!store.authoritative) { this.hitEffect(rock); return; }
    const result = this.world.damage(rock, amount, Math.round(Number(store.stats.values.splitBonus)));
    store.countBy('dealt', channel, amount);
    this.hitEffect(rock);
    if (crit || chance(0.5)) {
      this.fx.spawnText(rock.x, rock.y - rock.r - 4, fmt(amount), crit ? '#fff4a0' : '#ffffff', crit ? 13 : 11);
    }
    if (result.destroyed) this.onRockDestroyed(rock, true);
  }

  private explode(x: number, y: number, damage: number, radius: number, channel: string): void {
    sfx.crack();
    this.cameras.main.shake(120, 0.002);
    this.sparks.emitParticleAt(x, y, 10);
    if (!store.authoritative) return;
    // Die Flaeche **verteilt**, sie reserviert nicht: sie trifft Gegner und —
    // ausserhalb des Gefechts — Brocken im selben Radius. Wer wie viel
    // abbekommt, entscheidet `sim/blast.ts`; hier stehen nur die beiden
    // Feldabfragen und der volle Schaden, den die Anteile skalieren.
    this.blastDamage = damage;
    this.blastChannel = channel;
    this.blastAt.x = x;
    this.blastAt.y = y;
    applyBlast(
      x, y, radius,
      this.hostiles.inRange(x, y, radius, this.hostileBuffer),
      this.world.queryCircle(x, y, radius, this.areaBuffer),
      this.ship.areaEngaged,
      this.blastSink,
    );
  }

  private onRockDestroyed(rock: Rock, award: boolean): void {
    sfx.crack();
    this.sparks.emitParticleAt(rock.x, rock.y, Math.min(16, 5 + Math.floor(rock.r / 5)));
    bus.emit('rock:destroyed', {
      id: rock.id, x: rock.x, y: rock.y, r: rock.r, ore: rock.ore, rich: rock.rich,
    });

    // Beide Ebenen — eine stehen gebliebene Adernebene waere ein leuchtendes
    // Netz ohne Brocken darunter.
    const sprite = this.rockSprites.get(rock.id);
    if (sprite) { sprite.destroy(); this.rockSprites.delete(rock.id); }
    const vein = this.veinSprites.get(rock.id);
    if (vein) { vein.destroy(); this.veinSprites.delete(rock.id); }

    if (!award) return;                        // online zahlt der Server aus
    if (rock.id.includes('#')) {
      store.count('fragments');
    } else {
      store.count('rocks');
      store.countBy('rocksByOre', rock.ore);
      store.countBy('rocksByKind', rock.kindId);
    }

    const v = store.stats.values;
    let amount = rock.oreAmount * v.yieldMul;
    if (chance(v.oreLuck)) amount *= 2;
    const parts = clamp(1 + Math.floor(rock.r / 18), 1, 4);
    for (let i = 0; i < parts; i++) this.fx.spawnDrop(rock.x, rock.y, rock.ore, amount / parts,
      resource(rock.ore)?.gem ?? false);

    // Der Ionensturm laedt die Kristallbildung auf — am Ort des **Brockens**,
    // Spiegel von `rules.award_rock` (der Wurf ist bewusst nicht deterministisch).
    const gemMul = this.anomalies.effectAt(rock.x, rock.y).gemLuckMul;
    if (!resource(rock.ore)?.gem && chance(v.gemLuck * gemMul * (rock.rich ? 3 : 1))) {
      this.fx.spawnDrop(rock.x, rock.y, 'crystal', (1 + rock.zoneIdx) * v.yieldMul * 0.5 + 1, true);
    }
    const levels = store.addXp(rock.xp * v.xpMul);
    if (levels > 0) this.onLevelUp(levels);
    bus.emit('state:changed', undefined);
  }

  private collect(res: string, amount: number, gem: boolean, display: boolean): void {
    // Zwei Sperren gegen Doppelbuchung: die Autoritaetsweiche und die Marke am
    // Drop selbst. Online hat der Server schon gebucht — der Drop ist Zierde.
    if (store.authoritative && !display) store.add(res, amount);
    store.count('drops');
    // Jeder eingesammelte Posten sagt, was er war. Vorher sah man nur, dass die
    // Zahl im HUD sprang — das ist keine Rueckmeldung, das ist Buchhaltung.
    this.fx.spawnText(this.ship.x, this.ship.y - (gem ? 18 : 12),
      `+${fmt(amount)} ${resource(res)?.name ?? res}`,
      resource(res)?.color ?? '#fff', gem ? 12 : 10);
    bus.emit('res:gained', { res });
    if (gem) sfx.gem();
    else sfx.pickup();
    if (!store.save.seen['res_' + res]) {
      store.save.seen['res_' + res] = true;
      bus.emit('toast', { text: `Neue Ressource: ${resource(res)?.name ?? res}`, tone: 'gold' });
      sfx.quest();
    }
    bus.emit('state:changed', undefined);
  }

  /**
   * Die Signaltaste (`X`).
   *
   * Online geht nur die **Absicht** ueber die Leitung ({t:'signal'}, FG-32);
   * gezeichnet wird das Echo der eigenen Sektorgruppe — eine Wahrheit, nicht
   * zwei: was die Mitspieler sehen, sieht auch der Absender. Solo gibt es kein
   * Echo, also zeichnet der Client direkt.
   */
  /**
   * Die Solarwind-Peilung — **auf Tastendruck**, nicht in der Spielschleife.
   *
   * Der Ringscan rechnet je nach Ausbaustufe ein paar hundert bis ein paar
   * tausend Chunks durch. Sechzigmal pro Sekunde waere das der teuerste Posten
   * des Spiels, einmal je Druck ist es unmerklich (Gate FG-14).
   *
   * Vor der Freischaltung tut die Taste **nichts** und wird nirgends beworben.
   * Ohne Fund kommt eine Meldung: ein stummer Tastendruck sieht aus wie ein
   * Fehler.
   */
  private takeBearing(): void {
    if (!store.stats.unlocks.bearing) return;
    // Geklemmt auf die Alphawand: eine Front dahinter waere ein Pfeil auf einen
    // Ort, an den niemand fliegen kann (`bearingRadius`).
    const range = bearingRadius(store.stats);
    const fund = nearestFront(this.ship.x, this.ship.y, this.world.seed, range, this.world.era);
    if (!fund) {
      bus.emit('toast', {
        text: `Peilung ohne Fund — keine Solarwind-Front in ${distStr(range)}`, tone: '',
      });
      return;
    }
    // Oberflaechenzustand, kein Fortschritt: er gehoert nicht in den Spielstand
    // (Gate FG-28) und ueberlebt bewusst kein Neuladen.
    this.bearing = fund;
    this.bearingUntil = this.elapsed + BEARING_SECONDS;
    bus.emit('toast', { text: `Solarwind-Front gepeilt — ${distStr(fund.dist)}`, tone: 'gold' });
    sfx.ping();
  }

  private sendSignal(): void {
    if (this.socket?.connected) {
      this.socket.sendSignal();
      return;
    }
    this.showSignal(this.ship.x, this.ship.y, null);
  }

  /** Ein Signal zeichnen: gestaffelte Ringe, fremde mit Absendername. */
  private showSignal(x: number, y: number, name: string | null): void {
    this.fx.spawnRing(x, y, 90, 0x8ff5ff, 0.6);
    this.fx.spawnRing(x, y, 170, 0x8ff5ff, 0.9);
    this.fx.spawnRing(x, y, 260, 0x63c8e8, 1.25);
    if (name !== null) this.fx.spawnText(x, y - 24, name, '#8ff5ff', 12);
    sfx.ping();
  }

  private onLevelUp(_levels: number): void {
    store.refresh();
    sfx.level();
    this.fx.spawnRing(this.ship.x, this.ship.y, 160, 0xa9f193, 0.7);
    bus.emit('toast', { text: `Schiffslevel ${store.save.ship.level}!`, tone: 'good' });
    this.applyZoom();
  }

  private hitEffect(rock: Rock): void {
    sfx.hit();
    this.sparks.emitParticleAt(rock.x, rock.y, 2);
  }

  private announceZone(dist: number): void {
    const index = zoneAtDist(dist, store.save.era);
    if (index === this.lastZone) return;
    if (this.lastZone >= 0 && index > this.lastZone && !store.save.seen['zone_' + index]) {
      store.save.seen['zone_' + index] = true;
      const name = zonesFor(store.save.era)[index].name;
      bus.emit('toast', { text: `Neue Zone: ${name}`, tone: 'gold' });
      sfx.quest();
    }
    this.lastZone = index;
  }

  /** Betreten und Verlassen einer Blase melden — einmal, nicht je Frame. */
  private announceAnomaly(): void {
    const bubble = this.anomalies.at(this.ship.x, this.ship.y);
    const kind = bubble ? anomalyKind(bubble.kindId) : undefined;
    const name = kind?.name ?? null;
    if (name === this.lastAnomaly) return;
    this.lastAnomaly = name;
    if (kind) bus.emit('toast', { text: `${kind.name} — ${kind.desc}`, tone: '' });
  }

  /** Fuer das HUD: Name der Blase, in der das Schiff gerade steht. */
  activeAnomaly(): string | null {
    return this.lastAnomaly;
  }

  /**
   * Melden, wer gerade auf das Schiff feuert.
   *
   * Seit der Abbau im Gefecht pausiert, ist das keine Verzierung: der
   * Wachbereich einer Schwarmmutter misst 780 Einheiten, und ohne diese Meldung
   * stünde ein Spieler darin, sähe seinen Abbau aussetzen und fände keinen
   * Grund. Beide Seiten rechnen dieselbe Antwort aus Standort und `reach` — es
   * braucht dafür keine Nachricht (Gate FG-32).
   */
  private announceCombat(): void {
    const feinde = this.hostiles.engaging(this.ship.x, this.ship.y, this.engagedBuffer);
    if (!feinde.length) {
      if (this.lastCombat !== 0) { this.lastCombat = 0; bus.emit('combat', { count: 0, name: '' }); }
      return;
    }
    let naechster = feinde[0];
    let bestDist = Infinity;
    for (const hostile of feinde) {
      const dist = (hostile.x - this.ship.x) ** 2 + (hostile.y - this.ship.y) ** 2;
      if (dist < bestDist) { bestDist = dist; naechster = hostile; }
    }
    const name = enemy(naechster.kindId)?.name ?? naechster.kindId;
    const key = feinde.length * 31 + name.length;
    if (key === this.lastCombat) return;
    this.lastCombat = key;
    bus.emit('combat', { count: feinde.length, name });
  }

  private warnOnEmpty(): void {
    if (this.ship.empty && !this.warnedEmpty) {
      this.warnedEmpty = true;
      bus.emit('toast', { text: 'Zellen leer — zurück zur Kapsel zum Aufladen', tone: 'gold' });
    } else if (!this.ship.empty && store.save.ship.energy > Number(store.stats.values.energyMax) * 0.3) {
      this.warnedEmpty = false;
    }
  }

  // ── Zeichnen (lesend, Gate FG-17) ──────────────────────────────────────────
  private syncView(dt: number): void {
    const cam = this.cameras.main;
    const targetX = this.ship.x + this.ship.vx * CAM_LEAD;
    const targetY = this.ship.y + this.ship.vy * CAM_LEAD;
    const k = 1 - Math.exp(-6 * dt);
    cam.centerOn(
      cam.midPoint.x + (targetX - cam.midPoint.x) * k,
      cam.midPoint.y + (targetY - cam.midPoint.y) * k,
    );

    this.syncBackground();
    this.syncPlanets();
    this.syncAnomalies();
    // Brocken und Gegner zeichnen ihre Lebensbalken in dieselbe Flaeche und
    // cullen dafuer schon selbst — geleert wird sie deshalb hier, einmal, vor
    // beiden.
    this.healthGfx.clear();
    this.syncRocks();
    this.syncHostiles();
    this.syncProbes();
    this.syncCaches();
    this.syncGear();
    this.syncArm();
    this.syncShip();
    this.syncPeers();
    this.syncDrops();
    this.syncMissiles();
    this.syncHostileShots();
    this.syncBeams();
    this.syncTexts();
    this.syncWall();
    this.syncBase();
  }

  /**
   * Die Planeten im Heimatsystem — Kugeln an ihrem Ort, hinter allem anderen.
   *
   * Sie stehen in **Weltkoordinaten**, ohne Parallaxe: die Umlaufbahn ist ein
   * Ort, und eine Kugel, die gegen das Feld wandert, zeigte ihn falsch an.
   * Skaliert wird **ganzzahlig** (`PLANET_TEXEL`), wie es der Pixel-Look
   * verlangt. Die Texturen entstehen erst hier, beim ersten Bild im
   * Heimatsystem — wer in Akt I spielt, braucht keine davon.
   */
  private syncPlanets(): void {
    const sichtbar = store.save.era >= balancing().colony.minEra;
    if (!sichtbar) {
      for (const sprite of this.planetSprites) sprite.setVisible(false);
      return;
    }
    if (this.planetSprites.length !== planets().length) {
      ensurePlanetTextures(this);
      for (const sprite of this.planetSprites) sprite.destroy();
      this.planetSprites.length = 0;
      for (const planet of planets()) {
        this.planetSprites.push(this.add.image(0, 0, planetTextureKey(planet.id)).setDepth(-15).setScale(PLANET_TEXEL));
      }
    }
    const cam = this.cameras.main;
    const sicht = cam.worldView;
    planets().forEach((planet, i) => {
      planetAt(planet.id, this.world.seed, store.save.era, this.planetPoint$);
      const rand = planet.radius * 1.3;
      const imBild = this.planetPoint$.x + rand > sicht.x && this.planetPoint$.x - rand < sicht.right
        && this.planetPoint$.y + rand > sicht.y && this.planetPoint$.y - rand < sicht.bottom;
      this.planetSprites[i].setVisible(imBild).setPosition(this.planetPoint$.x, this.planetPoint$.y);
    });
  }

  private syncBackground(): void {
    if (this.backdrop) this.syncBackdropShader();
    else this.syncBackdropTiles();
  }

  /**
   * Der gerechnete Hintergrund: der Verlauf liegt in der **Welt**.
   *
   * Der Quad steht in Weltkoordinaten und deckt genau das Sichtfeld ab; der
   * Shader bekommt seine linke obere Ecke und seine Kantenlaenge in Metern und
   * rechnet daraus je Bildpunkt den Abstand zur Kapsel. Bewegt sich nur die
   * Kamera, aendert sich an einem Weltort nichts.
   */
  private syncBackdropShader(): void {
    const backdrop = this.backdrop;
    if (!backdrop) return;
    const cam = this.cameras.main;
    const breite = cam.width / cam.zoom;
    const hoehe = cam.height / cam.zoom;

    backdrop.setPosition(cam.midPoint.x, cam.midPoint.y);
    backdrop.setDisplaySize(breite, hoehe);
    this.world0[0] = cam.midPoint.x - breite / 2;
    this.world0[1] = cam.midPoint.y - hoehe / 2;
    this.worldSize[0] = breite;
    this.worldSize[1] = hoehe;
    backdrop.setUniform('uWorld0', this.world0);
    backdrop.setUniform('uWorldSize', this.worldSize);

    backdrop.setUniform('uNebulaUV', layerUV(
      cam.scrollX, cam.scrollY, cam.zoom, NEBULA_PARALLAX, NEBULA_TILE, this.uvNebula,
    ));
    backdrop.setUniform('uStars0UV', layerUV(
      cam.scrollX, cam.scrollY, cam.zoom, STAR_FAR_PARALLAX, STAR_TILE, this.uvStars0,
    ));
    backdrop.setUniform('uStars1UV', layerUV(
      cam.scrollX, cam.scrollY, cam.zoom, STAR_NEAR_PARALLAX, STAR_TILE, this.uvStars1,
    ));

    const fenster = zoneWindowAt(cam.midPoint.x, cam.midPoint.y, this.zoneWin, this.world.era);
    backdrop.setUniform('uFrom', fenster.from);
    backdrop.setUniform('uSky', fenster.sky);
    backdrop.setUniform('uStar', fenster.star);
    backdrop.setUniform('uNeb', fenster.nebula);
  }

  /**
   * Der Rueckfall ohne WebGL: weiterhin ein globaler Nebel, aber **ohne**
   * harten Wechsel.
   *
   * Die Farben kommen aus derselben reinen Funktion wie im Shader — nur einmal
   * je Bild statt je Bildpunkt. Der Nebel ist jetzt eine Struktur in Weiss, die
   * Zonenfarbe also ein mischbarer Tint statt einer zweiten Textur.
   */
  private syncBackdropTiles(): void {
    const cam = this.cameras.main;
    const farben = zoneColorsAt(cam.midPoint.x, cam.midPoint.y, this.zoneBuf, this.world.era);
    cam.setBackgroundColor(farben.sky);

    this.nebula.setTilePosition(cam.scrollX * NEBULA_PARALLAX, cam.scrollY * NEBULA_PARALLAX);
    this.starsFar.setTilePosition(cam.scrollX * STAR_FAR_PARALLAX, cam.scrollY * STAR_FAR_PARALLAX);
    this.starsNear.setTilePosition(cam.scrollX * STAR_NEAR_PARALLAX, cam.scrollY * STAR_NEAR_PARALLAX);
    this.nebula.setTint(farben.nebula);
    this.starsFar.setTint(farben.star);
    this.starsNear.setTint(farben.star);
  }

  private syncRocks(): void {
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2 + 90;
    const halfH = cam.height / cam.zoom / 2 + 90;
    const seen = new Set<string>();

    this.world.forEachInRect(
      cam.midPoint.x - halfW, cam.midPoint.y - halfH,
      cam.midPoint.x + halfW, cam.midPoint.y + halfH,
      (rock) => {
        seen.add(rock.id);
        // Die Textur ist grau — Erzfarbe und Schadensfaerbung stecken in einer
        // vorberechneten Zahl (siehe `rockTints`).
        const hp = this.world.hpOf(rock);
        const tint = this.rockTints.get(rock.ore);
        const farbe = tint ? tint[hp < rock.maxHp * 0.35 ? 1 : 0] : 0xffffff;

        // Der Winkel ist eine **Rechnung**, kein Zustand: `rock.rot` bleibt
        // unangetastet, und die Uhr laeuft in `step` (Gate FG-17).
        const winkel = rock.rot + rock.rotv * this.elapsed;
        this.syncRockBody(rock, farbe, winkel);
        this.syncRockVeins(rock, winkel);

        if (!rockHasBar(rock.kindId)) return;
        const alpha = barAlpha(this.world.hitAgeOf(rock));
        if (alpha <= 0) return;
        drawHealthBar(this.healthGfx, rock.x, rock.y, rock.r, hp / rock.maxHp, alpha);
      },
    );

    for (const [id, sprite] of this.rockSprites) {
      if (!seen.has(id)) { sprite.destroy(); this.rockSprites.delete(id); }
    }
    for (const [id, sprite] of this.veinSprites) {
      if (!seen.has(id)) { sprite.destroy(); this.veinSprites.delete(id); }
    }
  }

  /**
   * Der Koerper eines Brockens.
   *
   * Form und Spiegelung kommen aus der Brocken-ID: derselbe Brocken sieht nach
   * jedem Neuladen gleich aus, und im Protokoll steht dafuer kein Feld. Beides
   * aendert sich nie — deshalb steht es in der Einrichtung und nicht im Frame.
   */
  private syncRockBody(rock: Rock, farbe: number, winkel: number): void {
    let sprite = this.rockSprites.get(rock.id);
    if (!sprite) {
      const flip = flipOf(rock.id);
      sprite = this.add
        .image(rock.x, rock.y, ROCK_SHEET_KEY, variantOf(rock.id))
        .setDepth(ROCK_BODY_DEPTH)
        .setFlip((flip & 1) !== 0, (flip & 2) !== 0);
      this.rockSprites.set(rock.id, sprite);
    }
    sprite.setPosition(rock.x, rock.y);
    sprite.setRotation(winkel);
    sprite.setScale((rock.r * 2) / ROCK_FRAME);
    // Ein reicher Brocken ist dieselbe Form in dunklerer Toenung — der Gegensatz
    // zu seinen hellen Adern traegt den Unterschied, nicht ein zweites Bild.
    sprite.setTint(rock.rich ? mulHex(farbe, ROCK_RICH_TINT) : farbe);
  }

  /**
   * Die Erzadern als **zweite**, additiv ueberblendete Ebene.
   *
   * Drei Dinge daran sind Regel:
   *
   * - Sie liegen auf einer **eigenen** Tiefe. Ein Wechsel des Blend-Modus bricht
   *   den Zeichenstapel; Koerper und Adern abwechselnd zu zeichnen kostete je
   *   Brocken zwei Flushes und machte den ganzen Umbau weg vom Shader zunichte.
   * - Sie nehmen die **unbeschaedigte** Erzfarbe. Additiv aufgelegt ueberleben
   *   sie damit den warmen Schadensschleier: der daempft die Farbe, nicht den
   *   Kontrast — auch ein fast zerstoerter Brocken verraet sein Erz (FG-42).
   * - Sie sitzen **innerhalb** der Silhouette (`VEIN_INSET`), sonst stuenden
   *   leuchtende Faeden im leeren Raum neben dem Brocken.
   *
   * **Kein Rueckfall.** Ein Erz ohne Adernbild soll auffallen und nicht still
   * als Standardgestein durchgehen — genau die Falle, die beim Icon-Namen ein
   * Schloss rendert (FG-25).
   */
  private syncRockVeins(rock: Rock, winkel: number): void {
    let sprite = this.veinSprites.get(rock.id);
    if (!sprite) {
      const vein = this.rockVeins.get(rock.ore);
      if (!vein) throw new Error(`Kein Adernbild für Erz „${rock.ore}"`);
      const flip = flipOf(rock.id);
      sprite = this.add
        .image(rock.x, rock.y, VEIN_SHEET_KEY, vein.frame)
        .setDepth(ROCK_VEIN_DEPTH)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setFlip((flip & 1) !== 0, (flip & 2) !== 0)
        .setTint(vein.tint)
        .setAlpha(rock.rich ? vein.alphaRich : vein.alpha);
      this.veinSprites.set(rock.id, sprite);
    }
    sprite.setPosition(rock.x, rock.y);
    sprite.setRotation(winkel);
    sprite.setScale((rock.r * 2 * VEIN_INSET) / VEIN_FRAME);
  }

  private syncShip(): void {
    // Das **Level** ist am Schiff sichtbar, der Ausbau an der Basis. Gewechselt
    // wird nur bei echter Aenderung — dasselbe Muster wie beim Zoomschluessel.
    const level = store.save.ship.level;
    const key = `${level}`;
    if (key !== this.shipKey) {
      const look = shipLook(level);
      this.shipSprite.setTexture(look.key, look.frame);
      this.shipSprite.setScale(shipRenderScale(level));
      this.shipKey = key;
    }

    this.shipSprite.setPosition(this.ship.x, this.ship.y);
    this.shipSprite.setRotation(this.ship.angle);
    this.shipSprite.setTint(this.ship.empty ? 0x8e8778 : 0xffffff);

    while (this.droneSprites.length < this.ship.drones.length) {
      const platz = this.droneSprites.length;
      const look = droneLook(level, platz);
      this.droneSprites.push(
        this.add.image(0, 0, look.key, look.frame).setDepth(DRONE_DEPTH).setScale(DRONE_SCALE),
      );
    }
    // Die Klasse haengt am Level, und das steigt mitten in der Sitzung: die
    // Textur wird deshalb nachgezogen, nicht nur beim Anlegen gesetzt. Dieselbe
    // Luecke, die Mitspieler frueher im alten Schiff fliegen liess.
    if (key !== this.droneKey) {
      for (let i = 0; i < this.droneSprites.length; i++) {
        const look = droneLook(level, i);
        this.droneSprites[i].setTexture(look.key, look.frame);
      }
      this.droneKey = key;
    }
    for (let i = 0; i < this.droneSprites.length; i++) {
      const drone = this.ship.drones[i];
      const sprite = this.droneSprites[i];
      sprite.setVisible(Boolean(drone));
      if (!drone) continue;
      sprite.setPosition(drone.x, drone.y);
      // Blickrichtung zum Ziel — rein lesend (FG-17). `tx/ty` liegt auf der
      // Drohne, damit der Zeichenpfad nichts nachschlagen und nichts rechnen
      // muss, was den Zustand beruehrt.
      if (drone.targetId) sprite.setRotation(Math.atan2(drone.ty - drone.y, drone.tx - drone.x));
      else sprite.setRotation(0);
    }
  }

  private syncDrops(): void {
    const list = this.fx.drops.active;
    while (this.dropSprites.length < list.length) {
      this.dropSprites.push(this.add.image(0, 0, 'spark').setDepth(15));
    }
    for (let i = 0; i < this.dropSprites.length; i++) {
      const sprite = this.dropSprites[i];
      const drop = list[i];
      if (!drop) { sprite.setVisible(false); continue; }
      sprite.setVisible(true);
      const key = dropTextureKey(drop.res);
      if (this.dropTextures[i] !== key) {
        this.dropTextures[i] = key;
        sprite.setTexture(key);
      }
      sprite.setPosition(drop.x, drop.y);
      sprite.setScale(((drop.r * 2) / 32) * (1 + Math.sin(drop.pulse) * 0.12));
    }
  }

  private syncMissiles(): void {
    const list = this.fx.missiles.active;
    while (this.missileSprites.length < list.length) {
      this.missileSprites.push(this.add.image(0, 0, 'missile').setDepth(18));
    }
    for (let i = 0; i < this.missileSprites.length; i++) {
      const sprite = this.missileSprites[i];
      const missile = list[i];
      if (!missile) { sprite.setVisible(false); continue; }
      sprite.setVisible(true);
      sprite.setPosition(missile.x, missile.y);
      sprite.setRotation(missile.angle);
    }
  }

  /** Gegnerschuesse zeichnen. Weist nichts zu ausser Sprite-Eigenschaften (FG-17). */
  private syncHostileShots(): void {
    const list = this.fx.shots.active;
    while (this.shotSprites.length < list.length) {
      this.shotSprites.push(
        this.add.image(0, 0, BOLT_SHEET_KEY, 0).setDepth(19).setScale(BOLT_SCALE),
      );
    }
    for (let i = 0; i < this.shotSprites.length; i++) {
      const sprite = this.shotSprites[i];
      const shot = list[i];
      if (!shot) { sprite.setVisible(false); continue; }
      sprite.setVisible(true);
      sprite.setPosition(shot.x, shot.y);
      sprite.setRotation(shot.angle);
      sprite.setTint(shot.color);
      // Die Sprites kommen aus einem Pool und wechseln den Schuetzen; der Frame
      // reist deshalb am Schuss mit, nicht am Sprite.
      sprite.setFrame(shot.frame);
    }
  }

  private syncBeams(): void {
    const g = this.beamGfx;
    g.clear();
    this.fx.beams.forEach((beam) => {
      const f = beam.life / beam.max;
      g.lineStyle(beam.width * (0.6 + f * 0.8), beam.color, 0.35 + 0.65 * f);
      g.lineBetween(beam.x1, beam.y1, beam.x2, beam.y2);
    });
    this.fx.rings.forEach((ring) => {
      const f = 1 - ring.life / ring.max;
      g.lineStyle(3 * (1 - f) + 1, ring.color, (1 - f) * 0.8);
      g.strokeCircle(ring.x, ring.y, ring.maxR * f);
    });

    // Laserreichweite — der Spieler soll sehen, was er trifft.
    const range = Number(store.stats.values.laserRange) * (1 + Math.sin(this.ship.rangePulse * 2.2) * 0.012);
    g.lineStyle(2.4, this.ship.empty ? 0x8e8778 : 0xff9a3c, this.ship.empty ? 0.4 : 0.75);
    g.strokeCircle(this.ship.x, this.ship.y, range);
  }

  private syncTexts(): void {
    const list = this.fx.texts.active;
    while (this.textObjects.length < list.length) {
      this.textObjects.push(
        this.add.text(0, 0, '', { fontFamily: '"Lucida Console", monospace', fontSize: '11px' })
          .setOrigin(0.5).setDepth(35),
      );
    }
    for (let i = 0; i < this.textObjects.length; i++) {
      const label = this.textObjects[i];
      const item = list[i];
      if (!item) { label.setVisible(false); continue; }
      label.setVisible(true);
      label.setText(item.text);
      // Nur bei echter Aenderung — sonst rastert Phaser die Textur neu.
      if (this.textColors[i] !== item.color) {
        this.textColors[i] = item.color;
        label.setColor(item.color);
      }
      if (this.textSizes[i] !== item.size) {
        this.textSizes[i] = item.size;
        label.setFontSize(item.size);
      }
      label.setPosition(item.x, item.y);
      label.setAlpha(clamp((item.life / item.max) * 1.6, 0, 1));
    }
  }

  /**
   * Die Alphawand — der Rand des Sektors.
   *
   * Sie wird erst sichtbar, wenn man nah genug ist: eine Linie am Horizont,
   * die mit der Tiefe heller wird. Es ist **keine Mauer** — man wird gebremst
   * und zurueckgeschoben, nicht gestoppt und nicht beschaedigt.
   */
  private syncWall(): void {
    const g = this.wallGfx;
    g.clear();

    const world = balancing().world;
    const dist = this.ship.distToBase;
    if (dist < world.fieldRadius - world.wallBand * 3) return;

    const tiefe = this.ship.wallDepth();
    g.lineStyle(3, 0xff6a5c, 0.25 + tiefe * 0.6);
    g.strokeCircle(0, 0, world.fieldRadius);
    g.lineStyle(1, 0xff6a5c, 0.12 + tiefe * 0.3);
    // Der aeussere Ring ist die Grenze des Erreichbaren — dieselbe Lesung, die
    // Karte und Peilung benutzen. Sie hier nochmal zu bilden waere die Kopie,
    // die still veraltet.
    g.strokeCircle(0, 0, worldLimit());
  }

  private syncBase(): void {
    const g = this.baseGfx;
    g.clear();

    const dock = balancing().dockRange;
    g.lineStyle(1.5, 0x5fe6d8, this.ship.docked ? 0.55 : 0.28);
    g.strokeCircle(0, 0, dock);

    // Die Kapsel wächst mit **ihrer Kette**, nicht mit der Gesamtzahl gebauter
    // Stufen: nach dem Sprung steht dort ein Trümmerfeld, und der Wiederaufbau
    // fängt wieder klein an. Ohne diese Rechnung stünde in Akt II sofort eine
    // Basis aus dreizehn Stufen — der Fortschrittsbalken, den man von überall sieht,
    // wäre am Ziel, bevor die erste Trümmerlage geräumt ist.
    const era = store.save.era;
    const level = chainLevel(era, store.save.built);
    const kette = chainLength(era);
    const radius = hullRadius(level);
    const half = radius * BASE_HULL_ASPECT;

    // Was noch nicht wieder steht, liegt daneben: ein Ring aus Trümmern, der
    // mit jeder Stufe **weniger wird**, nicht bloß blasser. Sonst leistete
    // ausgerechnet die Bergungswerft — „räumt die Trümmer der alten Basis" —
    // sichtbar nichts. Der Winkel bleibt an `i` gebunden, damit die
    // verbleibenden Brocken stehen bleiben, statt sich neu zu sortieren.
    const schutt = kette > 0 ? 1 - level / kette : 0;
    if (schutt > 0 && era > balancing().eraFirst) {
      g.fillStyle(BASE_DEBRIS_TINT, 0.3 + 0.35 * schutt);
      for (let i = 0; i < Math.ceil(BASE_DEBRIS_COUNT * schutt); i++) {
        const winkel = (i / BASE_DEBRIS_COUNT) * TAU;
        const weite = BASE_DEBRIS_RING + (i % 3) * 11;
        const groesse = 7 + (i % 4) * 4;
        g.fillRect(
          Math.cos(winkel) * weite - groesse / 2,
          Math.sin(winkel) * weite - groesse / 2,
          groesse, groesse,
        );
      }
    }

    g.fillStyle(BASE_SHADOW_TINT, 1);
    g.fillRect(-radius + 4, -half + 4, radius * 2, half * 2);
    g.fillStyle(BASE_HULL_TINT, 1);
    g.fillRect(-radius, -half, radius * 2, half * 2);
    g.fillStyle(BASE_EDGE_TINT, 1);
    g.fillRect(-radius, -half, radius * 2, 5);
    g.fillRect(-radius, half - 6, radius * 2, 6);
    g.lineStyle(2, BASE_SHADOW_TINT, 1);
    g.strokeRect(-radius, -half, radius * 2, half * 2);

    g.fillStyle(this.ship.docked ? BASE_DOCK_TINT : BASE_IDLE_TINT, 1);
    g.fillRect(-radius * 0.5, -half * 0.62, radius, half * 1.24);

    // Der Ring der abgeschlossenen Kette — in Akt I der Teilchenbeschleuniger,
    // in Akt II das Heimatfeuer. Sein Radius ist **fest**, wie die Anker der
    // Bauteile: an den Rumpf gebunden schöbe ihn jede Stufe nach außen.
    if (kette > 0 && level >= kette) {
      g.lineStyle(3, finalTint(era), 0.8);
      g.strokeCircle(0, 0, BASE_FINAL_RING);
    }

    this.syncModules(era, level);
    this.syncCompass();
    this.syncColossus();
  }

  /**
   * Die Bauteile der Basis — je gebauter Stufe eines (`VIEW-001`, Gate FG-45).
   *
   * Umgehaengt wird **nur bei echter Aenderung**: dasselbe Muster wie die
   * Schiffstextur in `syncShip`. Die Sichtbarkeit haengt damit an keinem
   * Ereignis, sondern allein am Spielstand — ein Reconnect mit drei
   * zusaetzlichen Stufen zeigt sie im naechsten Bild.
   */
  private syncModules(era: number, level: number): void {
    const key = `${era}:${level}`;
    if (key !== this.baseKey) {
      this.baseKey = key;
      const kette = chainBuilds(era, this.chainBuf);
      for (let i = 0; i < this.moduleSprites.length; i++) {
        const sprite = this.moduleSprites[i];
        const modul = i < level ? baseModule(kette[i]) : undefined;
        sprite.setVisible(Boolean(modul));
        if (!modul) continue;
        // Ein Bauteil ohne Platz im Raster traegt seine eigene Textur — dieselbe
        // Zeichnung, nur in einer eigenen Leinwand (`gfx/textures.ts`).
        if (modul.frame === null) sprite.setTexture(basePartKey(kette[i]));
        else sprite.setTexture(BASE_SHEET_KEY, modul.frame);
        sprite.setPosition(modul.x, modul.y);
        sprite.setAlpha(1);
        sprite.clearTint();
      }
    }
    this.syncRaise(level);
  }

  /**
   * Das frisch gebaute Bauteil faehrt an die Station heran.
   *
   * **Kein Aufskalieren.** Bei ganzzahligem Kamerazoom ist ein Texel exakt n
   * Bildpunkte; eine gebrochene Sprite-Skalierung machte daraus ungleich grosse
   * Pixel und braeche den Pixel-Look-Vertrag. Die Bewegung entsteht deshalb aus
   * ganzzahligem Versatz, Alpha und einem kurzen weissen Blitz.
   */
  private syncRaise(level: number): void {
    if (!this.raiseId || this.raiseFrom === 0) return;
    const index = this.raiseIndex;
    const modul = baseModule(this.raiseId);
    if (!modul || index < 0 || index >= level || index >= this.moduleSprites.length) return;

    const sprite = this.moduleSprites[index];
    const t = clamp((this.elapsed - this.raiseFrom) / BASE_RAISE_SECONDS, 0, 1);
    const weite = Math.hypot(modul.x, modul.y) || 1;
    sprite.setAlpha(t);
    sprite.setPosition(
      Math.round(modul.x + (modul.x / weite) * BASE_RAISE_SLIDE * (1 - t)),
      Math.round(modul.y + (modul.y / weite) * BASE_RAISE_SLIDE * (1 - t)),
    );
    // In Phaser 4 sind Tint-Farbe und Tint-Modus getrennt; `clearTint` setzt
    // beides zurueck. Ohne den Modus waere Weiss ein Multiply — also nichts.
    if (t < BASE_RAISE_FLASH) sprite.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
    else sprite.clearTint();
  }

  /** Der Pfeil zur Kapsel, solange sie ausserhalb des Sichtfelds liegt. */
  private syncCompass(): void {
    const g = this.compassGfx;
    g.clear();

    const cam = this.cameras.main;
    const mark = compassMark(
      cam.midPoint.x, cam.midPoint.y, 0, 0,
      cam.zoom, cam.width / 2, cam.height / 2, COMPASS_MARGIN,
    );
    if (!mark.onScreen) this.drawMark(mark, 0x5fe6d8, 1);
    this.syncBearing();
  }

  /**
   * Der Peilungspfeil — derselbe Zeiger, andere Farbe, mit Entfernung.
   *
   * Er verblasst nach `BEARING_SECONDS` und zeigt auch dann, wenn die Front
   * im Bild liegt: „da vorn" ist die Antwort auf die Frage, die den
   * Tastendruck ausgeloest hat.
   */
  private syncBearing(): void {
    const rest = this.bearingUntil - this.elapsed;
    if (!this.bearing || rest <= 0) {
      this.bearingLabel.setVisible(false);
      return;
    }
    const cam = this.cameras.main;
    const alpha = clamp(rest / 3, 0, 1);
    const mark = compassMark(
      cam.midPoint.x, cam.midPoint.y, this.bearing.x, this.bearing.y,
      cam.zoom, cam.width / 2, cam.height / 2, COMPASS_MARGIN,
    );
    this.drawMark(mark, BEARING_TINT, alpha);

    const inv = 1 / cam.zoom;
    this.bearingLabel.setVisible(true);
    this.bearingLabel.setAlpha(alpha);
    this.bearingLabel.setScale(inv);
    this.bearingLabel.setText(distStr(Math.hypot(
      this.bearing.x - this.ship.x, this.bearing.y - this.ship.y,
    )));
    this.bearingLabel.setPosition(
      cam.width / 2 + mark.x * inv,
      cam.height / 2 + mark.y * inv + 22 * inv,
    );
  }

  /**
   * Einen Zeiger an den Bildrand malen.
   *
   * `mark` rechnet in **Bildpunkten**, die Grafik haengt aber am Kamerabild
   * (`setScrollFactor(0)`) — und die Kamera skaliert auch das noch einmal mit
   * ihrem Zoom. Ohne das Herausteilen rechnete der Pfeil den Zoom doppelt und
   * sass bei Stufe 2 weit ausserhalb des Bildes.
   */
  private drawMark(mark: CompassMark, tint: number, alpha: number): void {
    const g = this.compassGfx;
    const cam = this.cameras.main;
    const inv = 1 / cam.zoom;
    const x = cam.width / 2 + mark.x * inv;
    const y = cam.height / 2 + mark.y * inv;
    const cos = Math.cos(mark.angle);
    const sin = Math.sin(mark.angle);
    const tip = 11 * inv;
    const back = 7 * inv;
    const side = 6.5 * inv;

    g.fillStyle(0x0e1218, 0.72 * alpha);
    g.fillCircle(x, y, 13 * inv);
    g.lineStyle(2 * inv, tint, 0.9 * alpha);
    g.strokeCircle(x, y, 13 * inv);
    g.fillStyle(tint, alpha);
    g.fillTriangle(
      x + cos * tip, y + sin * tip,
      x - cos * back - sin * side, y - sin * back + cos * side,
      x - cos * back + sin * side, y - sin * back - cos * side,
    );
  }

  /** Die Blasen als getoente Kreise — Umgebung unter den Brocken. */
  private syncAnomalies(): void {
    const g = this.anomalyGfx;
    g.clear();
    const cam = this.cameras.main;
    const halfW = cam.width / cam.zoom / 2;
    const halfH = cam.height / cam.zoom / 2;
    const puls = 1 + Math.sin(this.time.now / 600) * 0.02;
    for (const anomaly of this.anomalies.all()) {
      if (Math.abs(anomaly.x - cam.midPoint.x) > halfW + anomaly.r) continue;
      if (Math.abs(anomaly.y - cam.midPoint.y) > halfH + anomaly.r) continue;
      const tint = ANOMALY_TINT[anomaly.kindId] ?? 0xffffff;
      g.fillStyle(tint, 0.07);
      g.fillCircle(anomaly.x, anomaly.y, anomaly.r);
      g.lineStyle(2, tint, 0.35);
      g.strokeCircle(anomaly.x, anomaly.y, anomaly.r * puls);
    }
  }

  private syncColossus(): void {
    const g = this.overlayGfx;
    g.clear();
    const event = this.colossus;
    if (!event || event.x === undefined || event.y === undefined) return;
    const radius = event.r ?? balancing().colossus.radius;
    const share = event.maxHp ? clamp(event.hp / event.maxHp, 0, 1) : 1;

    g.fillStyle(0x241028, 0.85);
    g.fillCircle(event.x, event.y, radius);
    g.lineStyle(4, 0xc07ce0, 0.9);
    g.strokeCircle(event.x, event.y, radius);
    g.lineStyle(6, 0xff6a5c, 0.9);
    g.beginPath();
    g.arc(event.x, event.y, radius + 10, -Math.PI / 2, -Math.PI / 2 + TAU * share);
    g.strokePath();
  }

  /**
   * Eine Anwesenheitsnachricht: sie setzt **Ziele**, sie zeichnet nicht.
   *
   * Frueher stand hier das Setzen der Sprites — und weil die Nachricht nur
   * fuenfmal pro Sekunde kommt, sprangen fremde Schiffe genau so oft. Der
   * geglaettete Zustand liegt jetzt in `net/peers.ts`, gezeichnet wird je
   * Frame in `syncPeers`.
   */
  private onPresence(players: PlayerPresence[]): void {
    this.peerField.apply(players);
    bus.emit('net:status', {
      online: this.socket?.connected ?? false,
      players: this.peerField.count + 1,
      field: 'Randfeld',
    });
  }

  /** Zeichnen — liest nur (Gate FG-17). Fortgeschrieben wird in `step`. */
  private syncPeers(): void {
    const gesehen = new Set<string>();
    for (const peer of this.peerField.all()) {
      gesehen.add(peer.cid);
      // `lvl` ist alles, was von einem Mitspieler ankommt (FG-32) — und seit
      // das Schiffsbild am Level haengt, genuegt es: sein Schiff ist nicht mehr
      // geschaetzt, sondern richtig.
      let entry = this.peerSprites.get(peer.cid);
      if (!entry) {
        entry = {
          body: this.add.image(0, 0, shipLook(peer.lvl).key)
            .setDepth(29).setTint(0x9ff0ff),
          label: this.add.text(0, 0, peer.name, {
            fontFamily: '"Lucida Console", monospace', fontSize: '9px', color: '#9ff0ff',
          }).setOrigin(0.5, 1).setDepth(29),
          lvl: -1,
          name: peer.name,
        };
        this.peerSprites.set(peer.cid, entry);
      }
      entry.body.setPosition(peer.x, peer.y).setRotation(peer.angle).setAlpha(0.9 * peer.alpha);
      entry.label.setPosition(peer.x, peer.y - 14).setAlpha(peer.alpha);
      // Textur und Beschriftung nur bei echter Aenderung — `setTexture` und
      // `setText` bauen in Phaser neu auf, und das hier laeuft je Frame.
      // **Auch nach dem Anlegen**, nicht nur dabei: wer waehrend der Sitzung
      // aufsteigt, flog sonst bis zum Verbindungsabbruch im alten Schiff.
      if (entry.lvl !== peer.lvl) {
        const look = shipLook(peer.lvl);
        entry.body.setTexture(look.key, look.frame);
        entry.body.setScale(shipRenderScale(peer.lvl));
        entry.lvl = peer.lvl;
      }
      if (entry.name !== peer.name) {
        entry.label.setText(peer.name);
        entry.name = peer.name;
      }
    }
    for (const [cid, entry] of this.peerSprites) {
      if (gesehen.has(cid)) continue;
      entry.body.destroy();
      entry.label.destroy();
      this.peerSprites.delete(cid);
    }
  }

  /**
   * Die Ära des Spielstands übernehmen — der Schritt **nach** dem Heimflug.
   *
   * Ohne ihn bliebe das Feld stehen, wie es war: der Spielstand sagt Akt II,
   * die vier Erzeuger ziehen weiter aus der Saat von Akt I. Das ist kein
   * Fehler, den irgendetwas meldet — der Spieler bezahlt den Sprung, sieht die
   * Sequenz und fliegt danach durch dasselbe Randfeld.
   *
   * Online kommt der Neuaufbau **vom Server**: er liest beim Verbinden den
   * Spielstand und wählt daraus das Feld (`_resolve_field`). Deshalb wird die
   * Verbindung neu aufgebaut, statt die Ära lokal zu setzen — dieselbe
   * Bewegung wie bei `account:changed`, und aus demselben Grund.
   */
  enterEra(): void {
    this.hardReset();
    if (this.session.mode !== 'online') return;
    this.socket?.disconnect();
    this.socket = null;
    this.connect();
  }

  /** Neu anfangen — nur im Solo-Modus; online loescht der Server. */
  hardReset(): void {
    for (const sprite of this.rockSprites.values()) sprite.destroy();
    this.rockSprites.clear();
    for (const sprite of this.veinSprites.values()) sprite.destroy();
    this.veinSprites.clear();
    // Die Pool-Spiegel sind Hochwasserstaende: sie wuchsen mit dem dichtesten
    // Moment und schrumpften nie. Ohne dieses Aufraeumen laeuft jede
    // Zeichenschleife danach dauerhaft ueber die Maximallaenge.
    this.releaseSprites(this.dropSprites);
    this.releaseSprites(this.missileSprites);
    this.releaseSprites(this.shotSprites);
    this.releaseSprites(this.droneSprites);
    for (const label of this.textObjects) label.destroy();
    this.textObjects.length = 0;
    this.textColors.length = 0;
    this.textSizes.length = 0;
    this.dropTextures.length = 0;
    this.fx.clear();
    // Erst ans Dock, dann die Felder: `resetFields` laedt um das Schiff herum
    // vor, und das soll die Gegend sein, in der es gleich steht — nicht die,
    // die es gerade verlaesst.
    this.resetToDock();
    this.resetFields(balancing().world.defaultSeed, store.save.era, store.authoritative);
    for (const sprite of this.probeSprites.values()) sprite.destroy();
    this.probeSprites.clear();
    // Gegner und Mitspieler blieben hier frueher stehen: das Feld war neu (das
    // besorgt `resetFields`), die Sprites waren es nicht.
    for (const sprite of this.hostileSprites.values()) sprite.destroy();
    this.hostileSprites.clear();
    this.peerField.clear();
    for (const entry of this.peerSprites.values()) {
      entry.body.destroy();
      entry.label.destroy();
    }
    this.peerSprites.clear();
    this.cameras.main.centerOn(this.ship.x, this.ship.y);
    this.lastZone = -1;
    this.lastAnomaly = null;
    // Der leere Schluessel erzwingt einen vollstaendigen Neuaufbau der
    // Bauteile. Ohne ihn stuenden nach dem Sprung — oder nach „Neu anfangen" —
    // die Module der alten Kette weiter da: `${era}:${level}` kann danach
    // denselben Wert haben wie vorher.
    this.baseKey = '';
    this.raiseId = '';
    this.raiseArmed = 0;
    this.raiseFrom = 0;
    this.raiseIndex = -1;
  }

  private releaseSprites(list: Phaser.GameObjects.Image[]): void {
    for (const sprite of list) sprite.destroy();
    list.length = 0;
  }
}
